import dayjs from 'dayjs';
import autoTable from 'jspdf-autotable';
import ReportCoreMixin from '@shared_mixins/report/report.core.mixin.js';
import {getScore, getStateColor, importance_msg, category3_order} from '@shared/mixin/report/raw-data/hmc-2025';
import {mapGetters, mapState} from 'vuex';
import background_texture_img from '@shared/assets/img/background_texture.png';
import HYUNDAILogoPNG from '@shared/assets/img/hyundai_logo.png';
import KIALogoPNG from '@shared/assets/img/kia_logo.png';
import QESGLogoWhite from '@shared/assets/img/QuantifiedESG_Logo_v3_White.png';

export default {
    mixins: [
        ReportCoreMixin
    ],
    data() {
        return {
            hyundai_colors: {
                blue: '#002c5f',
                sand: '#e4dcd3',
                light_sand: '#f6f3f2',
                gold: '#a36b4f',
                gray: '#b1b1b1',
                muted: '#969696',
            },
            hyundai_colors_rgb: {
                blue: [0, 44, 95],
                sand: [228, 220, 221],
                light_sand: [246, 243, 242],
                gold: [163, 107, 79],
                muted: [150, 150, 150]
            },
            kia_colors: {
                midnight: '#05141F',
                red: '#EA0029',
                yellow: '#F3C300',
                dark_gray: '#6D6E71',
                light_gray: '#c7c8ca'
            },
            kia_colors_rgb: {
                midnight: [5, 20, 31],
                red: [234, 0, 41],
                dark_gray: [109, 110, 113],
                yellow: [243, 195, 0],
                gold: [163, 107, 79],
                light_gray: [199, 200, 202]
            },
            category1AreaChartInstance: null,
            category1BarChartInstance: null,
            PAR_BarChartInstance: null,
            PAR_Category1AreAChartInstance: null,
            category1PieChartInstance: null,
            category1PieChartInstanceImg: null,
            
            mapSubCodeToScoringPd: {},
            auditData: {},
            linkDestinationInfo: {
                toFactor: {},
                toAudit: {},
            },
            overAllScore: {
                total: null,
                social: null,
                environment: null
            }
        };
    },
    computed: {
        ...mapState('evaluations', ['audit_category']),
        ...mapGetters('evaluations', ['get_audit_category_name']),
        importance_sub_msg() {
            return importance_msg;
        },
        mapCategory0QCode() {
            return {
                'q84344058v2': {category: 's', order: 0}, // 인권경영
                'q668813246': {category: 's', order: 1}, // 공급망
                'q796000363': {category: 's', order: 2}, // 윤리경영
                'q94336947': {category: 's', order: 3}, // 안전보건경영
                'q857182297v2': {category: 'e', order: 4}, // 환경경영
                'q594534451': {category: 'e', order: 5}, // 기후변화
                'q164725453': {category: 'e', order: 6}, // 오염
                'q618728870': {category: 'e', order: 7}, // 물과 해양자원
                'q348485741': {category: 'e', order: 8}, // 생물 다양성과 생태계
                'q446387204': {category: 'e', order: 9}, // 자원사용과 순환경제
            };
        },
        headerInfo() {
            const _user_input_company_name = this.reportData.info.users.find(({criteria_name}) => criteria_name === '회사명');
            return {
                company_id: this.reportData?.info?.company.id,
                request_id: this.reportData?.info?.id,
                company_name: _user_input_company_name?.value || this.reportData?.info?.company?.company_name,
                vendor_code: this.reportData?.info?.vendor_code,
                published_date: this.reportData?.info?.completed_ts
            };
        },
        companyInfo() {
            
            if (!this.reportData?.parser.values.length) {
                return {
                    '매출액': ' - ',
                    '매출액_구분': ' - ',
                    '매출액_통화': ' - ',
                    '소재국가': ' - ',
                    '주요생산품목': ' - ',
                    '회사명': ' - ',
                    '근로자_정규직': ' - ',
                    '근로자_계약직': ' - ',
                    '근로자_계약직_파견': ' - ',
                    '근로자_타사소속': ' - ',
                    '근로자_정규직_외국인': ' - ',
                    '근로자_계약직_외국인': ' - ',
                    '근로자_계약직_파견_외국인': ' - '
                };
            } else {
                const _company_info_field = ['매출액', '매출액_구분', '매출액_통화', '소재국가', '주요생산품목', '회사명', '근로자_정규직', '근로자_계약직', '근로자_계약직_파견', '근로자_타사소속', '근로자_정규직_외국인', '근로자_계약직_외국인', '근로자_계약직_파견_외국인'];
                const _company_info = [...this.reportData?.parser.values].reduce((_info, _value) => {
                    if (_company_info_field.includes(_value.criteria_name)) {
                        _info[_value.criteria_name] = !_value.value.length ? ' - ' : _value.value[0];
                    } else {
                        _info[_value.criteria_name] = ' - ';
                    }
                    
                    return _info;
                }, {});
                
                const _company_info_exist_keys = Object.keys(_company_info);
                _company_info_field.forEach(_field => {
                    if (!_company_info_exist_keys.includes(_field)) {
                        _company_info[_field] = ' - ';
                    }
                });
                
                
                return _company_info;
            }
        },
        assessmentInfo() {
            let {
                service_type,
                requested_ts,
                in_progress_ts,
                in_assessment_ts,
                duedate_ts,
                completed_ts
            } = this.reportData.info;
            service_type = service_type === '일반' ? this.$t('일반 평가', this.report_lang) : this.$t('심화 평가', this.report_lang);
            
            let _assessmentInfo = {
                service_type: null, requested_date: requested_ts, duedate_date: duedate_ts, completed_date: completed_ts
            };
            
            if (service_type) _assessmentInfo.service_type = this.$t(service_type, this.report_lang);
            
            // 평가기간 실 평가기간으로 교체
            if (this.set_actual_duration) {
                _assessmentInfo.requested_date = this.report_lang === 'ko'
                    ? dayjs(in_progress_ts).format('YYYY년 MM월 DD일')
                    : dayjs(in_progress_ts).format('MM/DD/YYYY');
                
                _assessmentInfo.duedate_date = this.report_lang === 'ko'
                    ? dayjs(in_assessment_ts).format('YYYY년 MM월 DD일')
                    : dayjs(in_assessment_ts).format('MM/DD/YYYY');
            } else {
                if (_assessmentInfo.requested_date) {
                    _assessmentInfo.requested_date = this.report_lang === 'ko'
                        ? dayjs(_assessmentInfo.requested_date).format('YYYY년 MM월 DD일')
                        : dayjs(_assessmentInfo.requested_date).format('MM/DD/YYYY');
                } else {
                    _assessmentInfo.requested_date = ' - ';
                }
                
                if (_assessmentInfo.duedate_date) {
                    _assessmentInfo.duedate_date = this.report_lang === 'ko'
                        ? dayjs(_assessmentInfo.duedate_date).format('YYYY년 MM월 DD일')
                        : dayjs(_assessmentInfo.duedate_date).format('MM/DD/YYYY');
                } else {
                    _assessmentInfo.duedate_date = ' - ';
                }
                
                if (_assessmentInfo.completed_date) {
                    _assessmentInfo.completed_date = this.report_lang === 'ko'
                        ? dayjs(_assessmentInfo.completed_date).format('YYYY년 MM월 DD일')
                        : dayjs(_assessmentInfo.completed_date).format('MM/DD/YYYY');
                } else {
                    _assessmentInfo.completed_date = dayjs().format(this.report_lang === 'ko' ? 'YYYY년 MM월 DD일' : 'MM/DD/YYYY');
                }
            }
            
            return _assessmentInfo;
        },
        category1_description() {
            return {
                'q84344058v2': this.$t('인권경영 평가는 조직이 근로시간 관리, 차별 및 직장 내 괴롭힘 방지, 아동노동 및 강제노동 금지 등 인권을 보장하고 있는지, 또한 복지 및 역량강화 기회 등을 제공하고 있는지 확인합니다.', this.report_lang),  // 인권경영
                'q668813246': this.$t('공급망 평가는 협력사 관리부터 원자재 조달까지 전반적인 공급망 운영의 지속가능성을 평가합니다. 협력사 행동규범 보유 여부, 협력사 ESG 진단 뿐 아니라, 책임 있는 조달정책과 주요 원자재에 대한 점검 체계까지 포함하여 공급망 전반의 리스크 관리 역량을 종합적으로 확인합니다.', this.report_lang), // 공급망
                'q796000363': this.$t('윤리경영 평가는 기업이 투명하고 윤리적인 경영체계를 구축하고, 부패나 뇌물수수, 반경쟁 행위 등 비윤리적 행위를 관리·감독하며, 재발 방지를 위한 조치를 취하고 있는지 확인합니다.', this.report_lang), // 윤리경영
                'q94336947': this.$t('안전보건경영 평가는 기업이 산업재해 예방, 안전보건 교육, 작업장 위험성 평가 등을 통해 안전 리스크를 줄이고, 임직원의 건강과 복지 증진 활동을 체계적으로 추진하고 있는지 확인합니다.', this.report_lang), // 안전보건경영
                'q857182297v2': this.$t('환경경영 평가는 기업의 환경 방침, 법규 준수, 외부 인증, 성과지표(KPI) 등을 기반으로 환경 리스크 대응 체계와 개선 노력 수준을 평가합니다.', this.report_lang), // 환경경영
                'q594534451': this.$t('기후변화 평가는 온실가스 감축 전략, 배출량 관리(Scope 1~3), 목표 수립 및 에너지 소비관리 등 기업의 기후 리스크 대응 역량과 감축 활동 수준을 종합적으로 평가합니다.', this.report_lang), // 기후변화
                'q164725453': this.$t('오염 평가는 수질·대기·해양 오염과 화학물질 관리 전반에 대한 기업의 대응 체계를 평가합니다. 오염물질의 처리절차, 배출 통제, 유해물질 정보관리 및 방지대책 수립 여부 등을 중점적으로 살펴봅니다.', this.report_lang), // 오염
                'q618728870': this.$t('물과 해양자원 평가는 용수 사용, 수자원 보호, 폐수 배출 및 수질오염물질(BOD/COD) 관리 등 수자원의 지속가능한 이용과 오염 저감 노력을 평가합니다.', this.report_lang), // 물과 해양자원
                'q348485741': this.$t('생물다양성과 생태계 평가는 생물다양성 평가 및 보호조치, 유전자원의 활용과 안전관리, 그리고 동식물의 국제 이동에 관한 규정 준수 여부 등을 종합적으로 평가합니다.', this.report_lang), // 생물 다양성과 생태계
                'q446387204': this.$t('자원사용과 순환경제 평가는 폐기물의 분류, 처리, 재사용 관리 체계와 함께 유해폐기물 및 수출입 규정 준수 여부를 통해 자원순환 및 폐기물 저감 노력을 평가합니다.', this.report_lang), // 자원사용과 순환경제
            };
        },
        scoringInfo() {
            let _total_score = this.scoring.total_scoring_pd[0];
            _total_score['환산_점수'] = this.roundToOneDecimalPlace(_total_score['환산_점수']);
            
            let _category1_score = [];
            if (this.scoring?.category1_scoring_pd) {
                _category1_score = this.scoring?.category1_scoring_pd.reduce((_category1_score, _category1) => {
                    const _question_name = this.report_lang === 'ko'
                        ? _category1['category1_question_name']
                        : this.report_lang === 'en'
                            ? _category1['category1_question_name_ea']
                            : _category1['category1_question_name_ca'];
                    
                    _category1_score.push({
                        question_name: _question_name,
                        question_code: _category1['category1_question_code'],
                        question_name_ko: _category1['category1_question_name'],
                        'score': _category1['score'] || 0,
                        '문항_수': _category1['문항 수'] || 0,
                        '배점': _category1['배점'] || 0,
                        '배점_대비_점수_백분율': _category1['배점_대비_점수_백분율'] || 0,
                        '배점_합': _category1['배점_합'] || 0,
                        '가중치': this.roundToOneDecimalPlace(_category1['가중치'] || 0),
                        '접수_합': this.roundToOneDecimalPlace(_category1['score'] || 0),
                        '환산_점수': this.roundToOneDecimalPlace(_category1['환산_점수'] || 0)
                    });
                    return _category1_score;
                }, []);
            }
            
            let _category3_score = [];
            if (this.scoring?.category3_scoring_pd) {
                this.scoring?.category3_scoring_pd.sort((a,b) => {
                    return category3_order[a.category3_question_code].order - category3_order[b.category3_question_code].order;
                });
                _category3_score = this.scoring?.category3_scoring_pd.reduce((_category3_score, _category3) => {
                    const _category3_sub_scoring = this.reportData.scoring.sub_scoring_pd.filter((_category3_sub) => _category3_sub.category3_question_code === _category3.category3_question_code);
                    
                    let _is_category3_na = false;
                    if (_category3_sub_scoring.length) {
                        _is_category3_na = _category3_sub_scoring.every(({not_applicable}) => not_applicable === true);
                    }
                    
                    if (!_category3_score[_category3.category1_question_name]) {
                        const _category3_category1_score = this.scoring?.category1_scoring_pd.find(({category1_question_name}) => category1_question_name === _category3.category1_question_name);
                        // let { category1_question_code, category1_question_name, category1_question_name_ca, category1_question_name_ea, not_applicable_num, 환산_점수 } = _category3
                        let {
                            category1_question_code,
                            category1_question_name,
                            category1_question_name_ca,
                            category1_question_name_ea,
                        } = _category3;
                        환산_점수 = _is_category3_na ? null : this.roundToOneDecimalPlace(환산_점수 || 0);
                        
                        _category3_score[_category3.category1_question_name] = {
                            
                            category1_question_code,
                            category1_question_name_ko: category1_question_name,
                            category1_question_name: this.report_lang === 'ko'
                                ? category1_question_name
                                : this.report_lang === 'ca' ? category1_question_name_ca : category1_question_name_ea,
                            category1_score: this.roundToOneDecimalPlace(_category3_category1_score['환산_점수']),
                            min: this.roundToOneDecimalPlace(_category3_category1_score['환산_점수']),
                            max: null,
                            category3: []
                        };
                    }
                    
                    let {
                        category3_question_code,
                        category3_question_name,
                        category3_question_name_ca,
                        category3_question_name_ea,
                        환산_점수,
                    } = _category3;
                    
                    if (!_is_category3_na) {
                        환산_점수 = this.roundToOneDecimalPlace(환산_점수);
                        if (환산_점수 > 0) {
                            _category3_score[_category3.category1_question_name]['max'] = Math.max(_category3_score[_category3.category1_question_name]['max'], 환산_점수);
                        }
                        // _category3_score[_category3.category1_question_name]["max"] = Math.max(_category3_score[_category3.category1_question_name]["max"], 환산_점수)
                        _category3_score[_category3.category1_question_name]['min'] = Math.min(_category3_score[_category3.category1_question_name]['min'], 환산_점수);
                    }
                    
                    _category3_score[_category3.category1_question_name].category3.push({
                        category3_question_code,
                        category3_question_name: this.report_lang === 'ko'
                            ? category3_question_name
                            : this.report_lang === 'ca' ? category3_question_name_ca : category3_question_name_ea,
                        score: _is_category3_na ? null : this.roundToOneDecimalPlace(환산_점수 || 0),
                        not_applicable: _is_category3_na,
                    });
                    return _category3_score;
                }, {});
                
                Object.values(_category3_score).forEach((_category3_group) => {
                    _category3_group.min = _category3_group.max === _category3_group.min ? null : _category3_group.min;
                    _category3_group.category3.forEach(category3_ele => {
                        category3_ele.max = category3_ele.score === _category3_group.max;
                        category3_ele.min = category3_ele.score === _category3_group.min;
                    });
                    
                    _category3_group.min = _category3_group.min === null ? ' - ' : _category3_group.min;
                });
            }
            
            
            const category3Score = Object.values(_category3_score);
            category3Score.sort((a, b) => {
                return this.mapCategory0QCode[a.category1_question_code].order - this.mapCategory0QCode[b.category1_question_code].order;
            });
            
            return {
                total: _total_score,
                category1: _category1_score,
                category3: category3Score,
            };
        },
        par_score_analytics_guide_text() {
            const _converted_score_a = this.roundToOneDecimalPlace(this.scoring.par_scoring_pd.total_scoring_pd['A']['converted_score']);
            const _converted_score_cf = this.roundToOneDecimalPlace(this.scoring.par_scoring_pd.total_scoring_pd['CF']['converted_score']);
            const _converted_score_p = this.roundToOneDecimalPlace(this.scoring.par_scoring_pd.total_scoring_pd['P']['converted_score']);
            const _converted_score_pf = this.roundToOneDecimalPlace(this.scoring.par_scoring_pd.total_scoring_pd['PF']['converted_score']);
            return {
                'ko': `'정책' 유형은 ${_converted_score_p} 점, '조치' 유형은 ${_converted_score_a}점, '인증' 유형은 ${_converted_score_cf} 점, '성과' 유형은 ${_converted_score_pf} 점입니다.`,
                'en': `'Policy' type score is ${_converted_score_p}, 'Action' type score is ${_converted_score_a}, 'Certification' type score is ${_converted_score_cf}, and 'Performance' type score is ${_converted_score_pf}.`,
                'ca': `‘政策’类型的分数为${_converted_score_p}分，‘措施’类型的分数为${_converted_score_a}分，‘认证’类型的分数为${_converted_score_cf}分，‘绩效’类型的分数为${_converted_score_pf}分。`,
            };
        },
        keyFigureInfo_greengas() {
            if (!this.reportData?.parser.values.length) {
                return {
                    '3년간 온실가스 직접 배출량(Scope 1)': null,
                    '3년간 온실가스 간접 배출량(Scope 2)': null,
                    '온실가스 기타 간접 배출량(Scope 3)': null,
                };
            } else {
                const _key_figure_field = ['3년간 온실가스 직접 배출량(Scope 1)', '3년간 온실가스 간접 배출량(Scope 2)', '온실가스 기타 간접 배출량(Scope 3)'];
                const _key_fugure_greengas = [...this.reportData?.parser.values].reduce((_info, _value) => {
                    
                    if (_key_figure_field.includes(_value.criteria_name)) {
                        let _criteria_value = null;
                        if (_value.value.length) {
                            _criteria_value = {theme: _value.theme, unit: _value.unit, value: _value['key_value']};
                            _criteria_value['value'] = _criteria_value['value'].reduce((_value_obj, _value_ele) => {
                                _value_obj[_value_ele.key] = _value_ele.value;
                                return _value_obj;
                            }, {});
                        }
                        
                        _info[_value.criteria_name] = _criteria_value;
                    }
                    
                    return _info;
                }, {});
                
                _key_figure_field.forEach(_field => {
                    if (!_key_fugure_greengas[_field]) _key_fugure_greengas[_field] = null;
                });
                
                return _key_fugure_greengas;
            }
        },
        keyFigureInfo_LTIFR() {
            let _key_figure_LTIFR = {'근로손실재해율(LTIFR)': null};
            if (!this.reportData?.parser.values.length) {
                return _key_figure_LTIFR;
            } else {
                const target_figure_LTIFR = [...this.reportData?.parser.values].find(({criteria_name}) => criteria_name === '근로손실재해율(LTIFR)');
                if (!target_figure_LTIFR) {
                    return _key_figure_LTIFR;
                } else {
                    if (target_figure_LTIFR.key_value.length) {
                        _key_figure_LTIFR['근로손실재해율(LTIFR)'] = {};
                        target_figure_LTIFR.key_value.forEach(_value_ele => {
                            _key_figure_LTIFR['근로손실재해율(LTIFR)'][_value_ele.data_year] = _value_ele.value;
                        });
                        
                        return _key_figure_LTIFR;
                    } else {
                        return _key_figure_LTIFR;
                    }
                }
            }
        },
        keyFigureInfo_environment() {
            const _empty_value = {FY2022: ' - ', FY2023: ' - ', FY2024: ' - '};
            const _key_fugure_field_set = {
                '일반 폐기물 배출량': {theme: 'num', unit: 'ton', value: _empty_value},
                '유해 폐기물 배출량': {theme: 'num', unit: 'ton', value: _empty_value},
                '용수 취수량': {theme: 'num', unit: 'ton', value: _empty_value},
                'NOx(질소산화물) 배출량': {theme: 'num', unit: 'ton', value: _empty_value},
                'SOx(황산화물) 배출량': {theme: 'num', unit: 'ton', value: _empty_value},
                '먼지(PM) 배출량': {theme: 'num', unit: 'ton', value: _empty_value},
                '에너지 사용량': {theme: 'num', unit: 'MWh', value: _empty_value}
            };
            const sum_field = {
                '에너지': '에너지 사용량', '용수': '용수 취수량', '일반폐기물': '일반 폐기물 배출량', '유해폐기물': '유해 폐기물 배출량'
            };
            
            if (!this.reportData?.parser.values.length) {
                return _key_fugure_field_set;
            } else {
                const _key_figure_field = Object.keys(_key_fugure_field_set);
                const _key_figure_env = [...this.reportData?.parser.values].reduce((_info, _value) => {
                    Object.entries(sum_field).forEach(([key, value]) => {
                        if (_value.criteria_name.includes(key)) {
                            if (_info[value] == null) {
                                _info[value] = JSON.parse(JSON.stringify(_key_fugure_field_set[value]));
                            }
                            _value['key_value'].forEach(item => {
                                if (_info[value].value[item.key] === ' - ')
                                    _info[value].value[item.key] = 0;
                                //
                                
                                // Remove commas from numeric strings (e.g., "1,234" -> "1234")
                                const cleanValue = typeof item.value === 'string' 
                                    ? item.value.replace(/,/g, '').trim() 
                                    : item.value;
                                const target = parseFloat(cleanValue);
                                
                                if(!isNaN(target)) {
                                    // 부동소수점 연산 오류 방지: 덧셈 후 소수점 10자리로 반올림
                                    _info[value].value[item.key] = parseFloat((_info[value].value[item.key] + target).toFixed(10));
                                }
                                
                            });
                            
                        }
                        return _info;
                    });
                    if (_key_figure_field.includes(_value.criteria_name)) {
                        let _criteria_value = null;
                        if (_value.key_value.length) {
                            _criteria_value = {theme: _value.theme, unit: _value.unit, value: _value['key_value']};
                            _criteria_value['value'] = _criteria_value['value'].reduce((_value_obj, _value_ele) => {
                                _value_obj[_value_ele.key] = _value_ele.value;
                                return _value_obj;
                            }, {});
                        } else {
                            _criteria_value = {..._key_fugure_field_set[_value.criteria_name]};
                        }
                        
                        
                        _info[_value.criteria_name] = _criteria_value;
                    } else {
                        _info[_value.criteria_name] = {..._key_fugure_field_set[_value.criteria_name]};
                    }
                    
                    return _info;
                }, {});
                
                
                
                _key_figure_field.forEach(_key_name => {
                    if (!_key_figure_env[_key_name]) _key_figure_env[_key_name] = null;
                });
                
                return _key_figure_env;
            }
        },
    },
    watch: {},
    created() {
    },
    methods: {
        async createSummaryPage_v4(doc, position_y) {
            position_y = this.firstElementSpacing;
            position_y = this.addTitle(doc, position_y, 1, 'ESG 평가 결과 요약');
            
            // 기업정보 섹션
            position_y = this.addTitle(doc, position_y, 2, '기업 정보', {
                skip_divider: true,
                no_padding: true,
                no_margin: true
            });
            let _column_width = this.pageContentWidth / 4;
            
            const parseWorker = (worker_num) => {
                if (!worker_num || isNaN(Number(worker_num)) || worker_num === ' - ') return 0;
                if (worker_num && worker_num.includes(',')) worker_num = worker_num.replace(/,/g, '');
                return Number(worker_num);
            };

            const worker_num = parseWorker(this.companyInfo['근로자_정규직']) + parseWorker(this.companyInfo['근로자_계약직']) + parseWorker(this.companyInfo['근로자_계약직_파견']) + parseWorker(this.companyInfo['근로자_타사소속']);
            const foreign_worker_num = parseWorker(this.companyInfo['근로자_정규직_외국인']) + parseWorker(this.companyInfo['근로자_계약직_외국인']) + parseWorker(this.companyInfo['근로자_계약직_파견_외국인']);
            
            autoTable(doc, {
                theme: 'grid',
                startY: position_y,
                margin: this.jsPDFOpt.margin,
                // styles: this.jsPDFOpt.tableOpt.common,
                styles: this.tableStyles.global,
                body: [
                    [
                        {
                            content: this.$t('기업명', this.report_lang),
                            styles: {...this.tableStyles.th, cellWidth: _column_width}
                        },
                        {content: this.companyInfo['회사명'], styles: {...this.tableStyles.td, cellWidth: _column_width}},
                        {
                            content: this.$t('업체코드', this.report_lang),
                            styles: {...this.tableStyles.th, cellWidth: _column_width}
                        },
                        {
                            content: this.headerInfo.vendor_code,
                            styles: {...this.tableStyles.td, cellWidth: _column_width}
                        }
                    ],
                    [
                        {
                            content: this.$t('소재국가', this.report_lang),
                            styles: {...this.tableStyles.th, cellWidth: _column_width}
                        },
                        {content: this.companyInfo['소재국가'], styles: {...this.tableStyles.td, cellWidth: _column_width}},
                        {
                            content: this.$t('주요생산품목', this.report_lang),
                            styles: {...this.tableStyles.th, cellWidth: _column_width}
                        },
                        {
                            content: this.companyInfo['주요생산품목'],
                            styles: {...this.tableStyles.td, cellWidth: _column_width}
                        }
                    ],
                    [
                        {
                            content: this.$t('매출액', this.report_lang),
                            styles: {...this.tableStyles.th, cellWidth: _column_width}
                        },
                        {
                            content: `${this.formatNumber(this.companyInfo['매출액'])} ${this.$t(this.companyInfo['매출액_통화'], this.report_lang)} (${this.$t(this.companyInfo['매출액_구분'], this.report_lang)})`,
                            colSpan: 3,
                            styles: {...this.tableStyles.td, cellWidth: _column_width}
                        },
                    ],
                    [
                        {
                            content: this.$t('근로자수', this.report_lang),
                            styles: {...this.tableStyles.th, cellWidth: _column_width}
                        },
                        {
                            content: `${this.formatNumber(worker_num.toString())} ${this.$t('명', this.report_lang)} (${this.$t('정규직', this.report_lang)}: ${this.formatNumber(this.companyInfo['근로자_정규직'])}, ${this.formatNumber(this.$t('계약직', this.report_lang))}: ${this.companyInfo['근로자_계약직']}, ${this.formatNumber(this.$t('파견직', this.report_lang))}: ${this.companyInfo['근로자_계약직_파견']}, ${this.formatNumber(this.$t('타사소속', this.report_lang))}: ${this.companyInfo['근로자_타사소속']})`,
                            colSpan: 3,
                            styles: {...this.tableStyles.td, cellWidth: _column_width}
                        }
                    ],
                    [
                        {
                            content: this.$t('근로자수 (외국인)', this.report_lang),
                            styles: {...this.tableStyles.th, cellWidth: _column_width}
                        },
                        {
                            content: `${this.formatNumber(foreign_worker_num.toString())} ${this.$t('명', this.report_lang)} (${this.$t('정규직', this.report_lang)}: ${this.formatNumber(this.companyInfo['근로자_정규직_외국인'])}, ${this.formatNumber(this.$t('계약직', this.report_lang))}: ${this.companyInfo['근로자_계약직_외국인']}, ${this.formatNumber(this.$t('파견직', this.report_lang))}: ${this.companyInfo['근로자_계약직_파견_외국인']})`,
                            colSpan: 3,
                            styles: {...this.tableStyles.td, cellWidth: _column_width}
                        }
                    ],
                ],
                didDrawCell: (data) => this.jspdf_common_cell_font_convert(data)
            });
            
            
            position_y = doc.lastAutoTable.finalY + this.jsPDFOpt.spacing.section_xs;
            
            // 평가정보 섹션
            position_y = this.addTitle(doc, position_y, 2, this.$t('평가 정보', this.report_lang), {
                skip_divider: true,
                no_padding: true,
                no_margin: true
            });
            let _column_width_1 = _column_width;
            let _column_width_2 = this.pageContentWidth - _column_width_1;
            
            const getFormattedDate = (date) => {
                if (!date) return ' - ';
                return this.report_lang === 'ko'
                    ? dayjs(date).format('YYYY년 MM월 DD일')
                    : dayjs(date).format('MM/DD/YYYY');
            };
            
            let _assessment_info_table_body = [
                [
                    {
                        content: this.$t('평가기간', this.report_lang),
                        styles: {...this.tableStyles.th, cellWidth: _column_width_1}
                    },
                    // @todo 하드코딩 수정 필요.
                    {
                        content: `${getFormattedDate('2025-06-18')} ~ ${getFormattedDate('2025-08-08')}`,
                        // content: `${this.assessmentInfo.requested_date} ~ ${this.assessmentInfo.duedate_date}`,
                        styles: {...this.tableStyles.td, cellWidth: _column_width_2}
                    }
                ],
            ];
            
            if ((this.assessmentType && this.assessmentType === 'OSIS') || (!this.assessmentType && this.reportData.info.service_type === '심화')) {
                _assessment_info_table_body.push(
                    [
                        {
                            content: this.$t('평가유형', this.report_lang),
                            styles: {...this.tableStyles.th, cellWidth: _column_width_1}
                        },
                        {
                            content: this.$t('실사평가', this.report_lang),
                            styles: {...this.tableStyles.td, cellWidth: _column_width_2}
                        }
                    ]
                );
            }
            
            autoTable(doc, {
                theme: 'grid',
                startY: position_y,
                margin: this.jsPDFOpt.margin,
                styles: this.tableStyles.global,
                body: _assessment_info_table_body
            });
            position_y = doc.lastAutoTable.finalY + this.jsPDFOpt.spacing.section_xs;
            position_y = this.addTitle(doc, position_y, 2, this.$t('평가결과', this.report_lang));
            
            // this.setFont(doc, 'table_td', null, 'semibold')
            this.setFont(doc, 'table_td', null, 'semibold');
            if (this.report_lang === 'en') {
                doc.setFontSize(13.5);
                // doc.setFontSize(15)
            } else {
                doc.setFontSize(15);
                // doc.setFontSize(15)
            }
            
            // ESG 평가 총점
            position_y = this.addTitle(doc, position_y, 3, this.$t('ESG 평가 총점', this.report_lang), {
                skip_divider: true,
                no_margin: true
            });
            
            // 스코어박스 섹션
            const scorebox_height = this.report_lang === 'ko' ? 70 : 55;
            doc.setLineWidth(this.jsPDFOpt.card.lineWidth);
            doc.setDrawColor(this.jsPDFOpt.card.borderColor);
            doc.setFillColor(...this.jsPDFOpt.card.fillColor);
            doc.rect(this.jsPDFOpt.margin.left, position_y, this.pageContentWidth, scorebox_height, 'FD');
            position_y += scorebox_height;
            
            // 스코어박스 - 스코어 라벨(Total Score)
            if (this.report_lang === 'ca') {
                doc.setFont(this.jsPDFOpt.fontFamily_ca, 'normal');
            } else {
                doc.setFont(this.jsPDFOpt.fontFamily, 'normal');
            }
            
            doc.setFontSize(9.3);
            const _score_title_position_x = this.jsPDFOpt.margin.left + 10;
            const _score_title_position_y = (position_y - scorebox_height + 0.75) + this.jsPDFOpt.margin.top * 0.9;
            doc.text(this.report_lang === 'ca' ? '总分' : 'Total Score', _score_title_position_x, _score_title_position_y);
            
            
            // 스코어박스 - 스코어(점수)
            const total_score_width = this.pageContentWidth * 0.4;
            this.setFont(doc, 'cover_company_name');
            doc.setFontSize(30);
            
            const _score = this.scoringInfo.total['환산_점수'];
            const _score_text = this.report_lang !== 'ko' ? _score.toString() : `${_score} 점`;
            const _score_score = doc.splitTextToSize(_score_text, total_score_width);
            const _score_score_dim = doc.getTextDimensions(_score_score);
            const _score_position_x = _score_title_position_x + (((total_score_width - _score_title_position_x) - _score_score_dim.w) / 2);
            doc.text(_score_score, _score_position_x, position_y - (scorebox_height / 2) + (_score_score_dim.h / 2), {halign: 'center'});
            
            
            // 스코어박스 - 스코어바
            const scorebar_width = this.pageContentWidth * 0.6;
            const scorebar_height = 12;
            const scorebar_radius = 6;
            const scorebar_position_x = this.pageContentWidth * 0.425;
            const scorebar_position_y = position_y - (scorebox_height / 2) - (scorebar_height / 2);
            
            doc.setLineWidth(0);
            doc.setFillColor(228, 220, 211);
            doc.setDrawColor('#d6cdc4');
            doc.roundedRect(scorebar_position_x, scorebar_position_y, scorebar_width, scorebar_height, scorebar_radius, scorebar_radius, 'FD');
            
            // Draw the marker triangle
            const markerX = scorebar_position_x + ((_score / 100) * scorebar_width);
            doc.setFillColor(0, 0, 0);
            doc.triangle(markerX - 3, scorebar_position_y - 8, markerX + 3, scorebar_position_y - 8, markerX, scorebar_position_y - 3, 'F');
            
            // Draw the progress bar
            doc.setFillColor(...this.hyundai_colors_rgb.blue);
            doc.roundedRect(scorebar_position_x, scorebar_position_y, (_score / 100) * scorebar_width, scorebar_height, 5, 5, 'F');
            
            // Draw the Step Number
            doc.setFontSize(10);
            doc.setTextColor('#666666');
            doc.text('0', scorebar_position_x, scorebar_position_y + scorebar_height + 11);
            const _score_100_width = doc.getStringUnitWidth('100') * doc.getFontSize();
            doc.text('100', scorebar_position_x + scorebar_width - _score_100_width / 2, scorebar_position_y + scorebar_height + 11);
            
            doc.setFontSize(9);
            doc.setTextColor('#999999');
            for (let i = 10; i < 100; i += 10) {
                doc.text(`${i}`, scorebar_position_x + (i / 100) * scorebar_width, scorebar_position_y + scorebar_height + 11, null, null, 'center');
            }
            position_y += this.jsPDFOpt.spacing.section_md - 5;
            
            // ESG 평가 영역별 결과
            position_y = this.addTitle(doc, position_y, 3, this.$t('ESG 평가 영역별 결과', this.report_lang), {footnote: this.$t('(각 영역별 점수는 100점 만점 기준 환산 값)', this.report_lang)});
            
            const category0_data = this.scoringInfo.category1.reduce((_category0_data, _category1) => {
                const category0_index = this.mapCategory0QCode[_category1.question_code].category === 's' ? 0 : 1;
                
                
                _category0_data[category0_index].question_name = category0_index === 0 ? this.$t('사회', this.report_lang) : this.$t('환경', this.report_lang);
                _category0_data[category0_index].획득점수 += this.roundToOneDecimalPlace(_category1['score']);
                _category0_data[category0_index].배점 += this.roundToOneDecimalPlace(_category1['배점']);
                
                
                return _category0_data;
                
            }, [{score: 0,획득점수: 0, 배점: 0}, {score: 0,획득점수: 0, 배점: 0}]);
            
            category0_data[0].score = category0_data[0].획득점수 / category0_data[0].배점 * 100;
            category0_data[1].score = category0_data[1].획득점수 / category0_data[1].배점 * 100;
            
            // ESG 평가 영역별 테이블
            let _category1_score_table_name_head = category0_data.reduce((_added_category0_name, _category0) => {
                _added_category0_name.push({
                    content: this.splitByManagement(_category0.question_name),
                    styles: {...this.tableStyles.th, fontSize: 11}
                });
                return _added_category0_name;
            }, []);
            
            let _category1_score_table_rows = [[{
                content: this.$t('영역별 점수', this.report_lang),
                colSpan: 2,
                styles: {...this.tableStyles.th, fontSize: 12},
                minCellHeight: 25
            }], [..._category1_score_table_name_head]];
            
            const _total_score_cell_width = 0;
            const _category1_score_cell_width = (this.pageContentWidth - _total_score_cell_width) / 2;
            const _category1_score_cell_height = this.pageContentHeight - position_y - this.jsPDFOpt.footerHeight;
            const _category1_score_chart_height = _category1_score_cell_height * 0.75;
            const _category1_score_chart_width = _category1_score_chart_height;
            let _category1_score_font_size = Math.min(19, 12 * (_category1_score_chart_height / 53));
            _category1_score_font_size = Math.max(13, _category1_score_font_size);
            
            let _category1_score_table_body = [];
            
            for (const _category0 of category0_data) {
                if (this.category1PieChartInstance) {
                    this.category1PieChartInstance.destroy();
                    this.category1PieChartInstance = null;
                }
                
                
                _category1_score_table_body.push({
                    content: this.roundToOneDecimalPlace(_category0.score),
                    category1_cell: true,
                    category1_score: this.roundToOneDecimalPlace(_category0.score),
                    category1_img_url: await this.generateCategory1PieChartInstance([this.roundToOneDecimalPlace(_category0.score), 100 - this.roundToOneDecimalPlace(_category0.score)]),
                    category1_question_name: _category0.question_name,
                    styles: {
                        ...this.tableStyles.td,
                        minCellHeight: _category1_score_cell_height,
                        cellWidth: _category1_score_cell_width,
                        valign: 'middle',
                        cellPadding: {top: 3.5},
                        fontSize: _category1_score_font_size
                    }
                });
            }
            
            _category1_score_table_rows.push(_category1_score_table_body);
            
            autoTable(doc, {
                theme: 'grid',
                startY: position_y,
                margin: {...this.jsPDFOpt.margin, top: 0, left: this.jsPDFOpt.margin.left},
                styles: {...this.tableStyles.global, halign: 'center'},
                body: _category1_score_table_rows,
                didDrawCell: async (data) => {
                    if (data.cell.raw?.category1_cell && data.cell.raw?.category1_cell === true) {
                        const _category1_score_chart_positon_x = data.cell.x + ((_category1_score_cell_width - _category1_score_chart_width) / 2);
                        const _category1_score_chart_positon_y = data.cell.y + ((_category1_score_cell_height - _category1_score_chart_height) / 2); // + 0.5
                        
                        doc.addImage(data.cell.raw.category1_img_url, 'png', _category1_score_chart_positon_x, _category1_score_chart_positon_y, _category1_score_chart_width, _category1_score_chart_height);
                        if (this.category1PieChartInstance) {
                            this.category1PieChartInstance.destroy();
                            this.category1PieChartInstance = null;
                        }
                    }
                }
            });
            
            this.overAllScore.total = this.scoringInfo.total['환산_점수'];
            this.overAllScore.social = category0_data[0].score;
            this.overAllScore.environment = category0_data[1].score;
            
            position_y = doc.lastAutoTable.finalY + this.jsPDFOpt.spacing.section_xs;
            
            return position_y;
        },
        makeScoreLegend(doc, position_y){
            let _category3_table_legend_ele = (this.pageContentWidth / 3);
            const score_legend = [25, 50, 75, 100, 101];
            const legends_ko = score_legend.map((item) => {
                if (item === 101) {
                    return '100';
                }
                if (this.report_lang === 'ko')
                    return `${item} 미만`;
                else if (this.report_lang === 'ca')
                    return `小于${item}`;
                else
                    return `Below ${item}`;
            });
            let _longest_legend_position_x_weight = {
                ko: 30,
                en: 2,
                ca: 32
            };
            
            let _legend_mark_width = 7;
            let _legend_mark_position_y = position_y + 6;
            let _longest_legend_position_x = this.pageContentWidth / 2 + _longest_legend_position_x_weight[this.report_lang];
            score_legend.forEach((target_score, idx) => {
                this.setFont(doc, 'table_td', null, 'normal');
                doc.setFontSize(9);
                let _legend_name = 4 > idx
                    ? `${this.$t(legends_ko[idx], this.report_lang)}, `
                    : this.$t(legends_ko[idx], this.report_lang);
                _legend_name = doc.splitTextToSize(_legend_name, _category3_table_legend_ele);
                let _legend_name_dim = doc.getTextDimensions(_legend_name);
                
                doc.setFillColor(getStateColor(target_score - 1));
                doc.circle(_longest_legend_position_x, _legend_mark_position_y, 3, 'F');
                
                let _legend_name_position_x = _longest_legend_position_x + _legend_mark_width - 1;
                doc.text(`: ${_legend_name}`, _legend_name_position_x, _legend_mark_position_y + 2);
                _longest_legend_position_x += _legend_mark_width + _legend_name_dim.w + 5 + 5;
            });
        },
        generateScoreCardViewPage(doc, position_y) {
            this.addDocPage(doc);
            this.addHeader(doc);
            position_y = this.firstElementSpacing;
            
            
            
            position_y = this.addTitle(doc, position_y, 1, this.$t('지표/분야별 스코어 보드', this.report_lang));
            
            // title
            this.makeScoreLegend(doc, position_y);
            
            position_y = this.addTitle(doc, position_y, 2, this.$t('스코어 보드', this.report_lang), {
                skip_divider: true,
                no_margin: true
            });
            
            // table
            const _par_types = ['P', 'A', 'PF', 'CF'];
            const table_body = [];
            
            const parsed_scoring_pd = Object.entries(this.mapCategory0QCode).reduce((acc, [key, value]) => {
                acc[key] = {
                    P: {score: null, question_name: ''},
                    A: {score: null, question_name: ''},
                    PF: {score: null, question_name: ''},
                    CF: {score: null, question_name: ''},
                };
                return acc;
            }, {});
            
            _par_types.forEach((par_type) => {
                this.scoring.par_scoring_pd.category1_scoring_pd[par_type].map((_category1) => {
                    const score = _category1.converted_score;
                    
                    this.mapCategory0QCode[_category1.category1_question_code].question_name =
                        this.report_lang === 'ko' ? _category1.category1_question_name :
                            this.report_lang === 'ca' ? _category1.category1_question_name_ca : _category1.category1_question_name_ea;
                    
                    parsed_scoring_pd[_category1.category1_question_code][par_type].score = score;
                });
            });
            
            const head_font_size = this.report_lang === 'ko' ? 10.5 : 10;
            const category0_width = this.report_lang === 'en' ? this.pageContentWidth * 0.11 : this.pageContentWidth * 0.1;
            const category1_width = this.report_lang === 'en' ? this.pageContentWidth * 0.19 : this.pageContentWidth * 0.2;
            const thStyle = {
                ...this.datatables.th,
                lineWidth: 0.6,
                halign: 'center',
                fontSize: head_font_size,
            };
            
            const head = [
                [
                    {
                        content: this.$t('평가 영역', this.report_lang),
                        colSpan: 2,
                        styles: thStyle
                    },
                    {
                        content: this.$t('평가 지표', this.report_lang),
                        colSpan: 4,
                        styles: {...thStyle, minCellHeight: 24}
                    },
                    {
                        content: this.$t('영역 합계', this.report_lang),
                        rowSpan: 2,
                        styles: {...thStyle, cellWidth: this.pageContentWidth * 0.14}
                    }
                ],
                [
                    {
                        content: this.$t('영역', this.report_lang),
                        styles: {...thStyle, cellWidth: category0_width, minCellHeight: 24}
                    },
                    {
                        content: this.$t('세부 항목', this.report_lang),
                        styles: {...thStyle, cellWidth: category1_width, minCellHeight: 24}
                    },
                    {
                        content: this.$t('정책', this.report_lang),
                        styles: {...thStyle, cellWidth: this.pageContentWidth * 0.14, minCellHeight: 24}
                    },
                    {
                        content: this.$t('조치', this.report_lang),
                        styles: {...thStyle, cellWidth: this.pageContentWidth * 0.14, minCellHeight: 24}
                    },
                    {
                        content: this.$t('인증', this.report_lang),
                        styles: {...thStyle, cellWidth: this.pageContentWidth * 0.14, minCellHeight: 24}
                    },
                    {
                        content: this.$t('성과', this.report_lang),
                        styles: {...thStyle, cellWidth: this.pageContentWidth * 0.14, minCellHeight: 24}
                    },
                   
                ]
            ];
            
            
            const scoreTotal = {
                P: {score: 0, cnt: 0},
                A: {score: 0, cnt: 0},
                CF: {score: 0, cnt: 0},
                PF: {score: 0, cnt: 0},
            };
            Object.entries(parsed_scoring_pd).forEach(([qcode, category1], idx) => {
                table_body.push([
                    {
                        content: this.mapCategory0QCode[qcode].question_name,
                        styles: {...thStyle, minCellHeight: 30}
                    },
                    {content: getScore(category1.P.score), styles: {...this.datatables.td}, contentType: 'score'},
                    {content: getScore(category1.A.score), styles: {...this.datatables.td}, contentType: 'score'},
                    {content: getScore(category1.CF.score), styles: {...this.datatables.td}, contentType: 'score'},
                    {content: getScore(category1.PF.score), styles: {...this.datatables.td}, contentType: 'score'},
                ]);
                _par_types.forEach(_par_type => {
                    if (category1[_par_type].score != null) {
                        scoreTotal[_par_type].cnt += 1;
                        scoreTotal[_par_type].score += (category1[_par_type].score ?? 0);
                    }
                });
            });
            table_body[0].unshift({
                content: this.$t('사회', this.report_lang),
                rowSpan: 4,
                styles: thStyle,
            });
            table_body[0].push({
                content: getScore(this.overAllScore.social),
                rowSpan: 4,
                styles: {...this.datatables.td},
                contentType: 'score'
            });
            table_body[4].unshift({
                content: this.$t('환경', this.report_lang),
                rowSpan: 6,
                styles: thStyle,
            });
            table_body[4].push({
                content: getScore(this.overAllScore.environment),
                rowSpan: 6,
                styles: {...this.datatables.td},
                contentType: 'score'
            });
            
            table_body.push([
                {content: this.$t('총점', this.report_lang), styles: {...thStyle, minCellHeight: 30}, colSpan: 2},
                {content: getScore(this.overAllScore.total), styles: {...this.datatables.td}, colSpan: 5, contentType: 'score'},
            ]);
            
            autoTable(doc, {
                theme: 'grid',
                startY: position_y,
                margin: {...this.jsPDFOpt.margin, top: 0, left: this.jsPDFOpt.margin.left},
                styles: {...this.datatables.global, lineWidth: 0.4},
                head: head,
                body: table_body,
                willDrawCell: (data) => {
                    if (data.section === 'body') {
                        const {cell} = data;
                        
                        if (cell.raw?.contentType === 'score' && cell.raw?.content != null) {
                            cell.text = [];
                        }
                    }
                },
                didDrawCell: (data) => {
                    const { cell, row, column, section } = data;
                    const center_x = cell.x + cell.width / 2; // 셀의 가로 중앙
                    
                    // body
                    if (section === 'body' && column.index > 1) {
                        const cellData = cell.raw;
                        if (cellData && cellData.content !== null) {
                            
                            const y_position = cell.y + cell.height / 2;
                            
                            if (cellData.content === '-') {
                                doc.text(cellData.content.toString(), center_x, y_position + 3.5, {halign: 'center'});
                            } else {
                                doc.setFillColor(getStateColor(cellData.content));
                                doc.circle(center_x - 12, y_position, 4, 'F');
                                
                                this.setFont(doc, null, null, 'bold');
                                doc.setFontSize(12);
                                doc.setTextColor(30, 30, 30);
                                doc.text(cellData.content.toString(), center_x - 4, y_position + 3.5, {halign: 'center'});
                                
                                this.setFont(doc, null, null, 'normal');
                                doc.setTextColor('#292828');
                            }
                        }
                    }
                },
            });
            
            return position_y;
        },
        generatePartialSummaryPage1(doc, position_y) {
            this.addDocPage(doc);
            this.addHeader(doc);
            position_y = this.firstElementSpacing;
            
            
            position_y = this.addTitle(doc, position_y, 1, this.$t('영역별 평가결과 요약', this.report_lang));
            
            this.makeScoreLegend(doc, position_y);
            
            position_y += 20;
            
            const tableWidth = (this.pageContentWidth / 2) - 5;
            const _category1_group_left = this.scoringInfo.category1.filter(({question_code}) => this.mapCategory0QCode[question_code].category === 's');
            const _category1_group_right = this.scoringInfo.category1.filter(({question_code}) => this.mapCategory0QCode[question_code].category === 'e');
            _category1_group_left.sort((a, b) => this.mapCategory0QCode[a.question_code].order - this.mapCategory0QCode[b.question_code].order);
            _category1_group_right.sort((a, b) => this.mapCategory0QCode[a.question_code].order - this.mapCategory0QCode[b.question_code].order);
            
            
            // left
            let _social_group_position_y = position_y;
            let social_score = 0;
            let _social_question_name_score = '';
            const _social_table_body = [];
            
            for (const _category1 of _category1_group_left) {
                social_score += _category1['환산_점수'];
                
                _social_table_body.push([
                    {
                        content: _category1.question_name,
                        styles: {...this.tableStyles.td}
                    },
                    {
                        content: _category1['환산_점수'],
                        contentType: 'score',
                        styles: {...this.tableStyles.td}
                    },
                ]);
            }
            _social_table_body.unshift([
                {content: this.$t('대분류', this.report_lang), styles: {...this.tableStyles.th, cellWidth: tableWidth * 0.75}},
                {content: this.$t('점수', this.report_lang), styles: {...this.tableStyles.th, cellWidth: tableWidth * 0.25}},
            ]);
            
            _social_question_name_score = `${this.$t('사회', this.report_lang)}: ${this.roundToOneDecimalPlace(this.overAllScore.social)}`;
            if (this.report_lang === 'ko') {
                _social_question_name_score += '점';
            }
            if (this.report_lang === 'ca') {
                _social_question_name_score += '分';
            }
            
            _social_group_position_y = this.addTitle(doc, _social_group_position_y, 2, _social_question_name_score, {
                skip_divider: true,
                no_margin: true,
                position_x: this.jsPDFOpt.margin.left
            });
            
            
            autoTable(doc, {
                theme: 'grid',
                startY: _social_group_position_y,
                tableWidth: tableWidth,
                rowPageBreak: 'auto',
                margin: {...this.jsPDFOpt.margin, top: 0, left: this.jsPDFOpt.margin.left},
                styles: {...this.tableStyles.global, halign: 'center'},
                body: _social_table_body,
                willDrawCell: (data) => {
                    if (data.section === 'body') {
                        const {cell} = data;
                        
                        if (cell.raw?.contentType === 'score' && cell.raw?.content != null) {
                            cell.text = [];
                        }
                    }
                },
                didDrawCell: (data) => {
                    const { cell, row, column, section } = data;
                    const center_x = cell.x + cell.width / 2; // 셀의 가로 중앙
                    
                    // body
                    if (section === 'body' && column.index > 0) {
                        const cellData = cell.raw;
                        if (cell.raw?.contentType === 'score' && cell.raw?.content != null) {
                            
                            const y_position = cell.y + cell.height / 2;
                            
                            if (section === 'body' && column.index > 0) {
                                const cellData = cell.raw;
                                if (cell.raw?.contentType === 'score' && cell.raw?.content != null) {
                                    
                                    const y_position = cell.y + cell.height / 2;
                                    
                                    if (cellData.content === 'N/A') {
                                        this.setFont(doc, null, null, 'normal');
                                        doc.setFontSize(9);
                                        doc.setTextColor('#292828');
                                        doc.text(cellData.content.toString(), center_x - 8, y_position + 2.5, {halign: 'center'});
                                    } else {
                                        this.setFont(doc, null, null, 'bold');
                                        doc.setFontSize(10);
                                        doc.setTextColor('#292828');
                                        doc.setFillColor(getStateColor(cellData.content));
                                        doc.circle(center_x - 12, y_position, 2, 'F');
                                        
                                        
                                        doc.text(cellData.content.toString(), center_x - 4, y_position + 2.5, {halign: 'center'});
                                        
                                        this.setFont(doc, null, null, 'normal');
                                    }
                                }
                            }
                        }
                    }
                },
                didDrawPage: (HookData) => {
                    this.addHeader(HookData.doc);
                }
            });
            
            // right
            let _environment_group_position_y = position_y;
            let environment_score = 0;
            let _environment_question_name_score = '';
            const _environment_table_body = [];
            const _environment_table_right = (this.pageContentWidth / 2) + 5 + this.jsPDFOpt.margin.left;
            
            for (const _category1 of _category1_group_right) {
                environment_score += _category1['환산_점수'];
                
                _environment_table_body.push([
                    {
                        content: _category1.question_name,
                        styles: {...this.tableStyles.td}
                    },
                    {
                        content: _category1['환산_점수'],
                        contentType: 'score',
                        styles: {...this.tableStyles.td}
                    },
                ]);
            }
            _environment_table_body.unshift([
                {content: this.$t('대분류', this.report_lang), styles: {...this.tableStyles.th, cellWidth: tableWidth * 0.75}},
                {content: this.$t('점수', this.report_lang), styles: {...this.tableStyles.th, cellWidth: tableWidth * 0.25}},
            ]);
            
            _environment_question_name_score = `${this.$t('환경', this.report_lang)}: ${this.roundToOneDecimalPlace(this.overAllScore.environment)}`;
            if (this.report_lang === 'ko') {
                _environment_question_name_score += '점';
            }
            if (this.report_lang === 'ca') {
                _environment_question_name_score += '分';
            }
            
            _environment_group_position_y = this.addTitle(doc, _environment_group_position_y, 2, _environment_question_name_score, {
                skip_divider: true,
                no_margin: true,
                position_x: _environment_table_right
            });
            
            
            autoTable(doc, {
                theme: 'grid',
                startY: _environment_group_position_y,
                tableWidth: tableWidth,
                rowPageBreak: 'auto',
                margin: {...this.jsPDFOpt.margin, top: 0, left: _environment_table_right},
                styles: {...this.tableStyles.global, halign: 'center'},
                body: _environment_table_body,
                willDrawCell: (data) => {
                    if (data.section === 'body') {
                        const {cell} = data;
                        
                        if (cell.raw?.contentType === 'score' && cell.raw?.content != null) {
                            cell.text = [];
                        }
                    }
                },
                didDrawCell: (data) => {
                    const { cell, row, column, section } = data;
                    const center_x = cell.x + cell.width / 2; // 셀의 가로 중앙
                    
                    // body
                    if (section === 'body' && column.index > 0) {
                        const cellData = cell.raw;
                        if (cell.raw?.contentType === 'score' && cell.raw?.content != null) {
                            
                            const y_position = cell.y + cell.height / 2;
                            
                            if (section === 'body' && column.index > 0) {
                                const cellData = cell.raw;
                                if (cell.raw?.contentType === 'score' && cell.raw?.content != null) {
                                    
                                    const y_position = cell.y + cell.height / 2;
                                    
                                    if (cellData.content === 'N/A') {
                                        this.setFont(doc, null, null, 'normal');
                                        doc.setFontSize(9);
                                        doc.setTextColor('#292828');
                                        doc.text(cellData.content.toString(), center_x - 8, y_position + 2.5, {halign: 'center'});
                                    } else {
                                        this.setFont(doc, null, null, 'bold');
                                        doc.setFontSize(10);
                                        doc.setTextColor('#292828');
                                        doc.setFillColor(getStateColor(cellData.content));
                                        doc.circle(center_x - 12, y_position, 2, 'F');
                                        
                                        
                                        doc.text(cellData.content.toString(), center_x - 4, y_position + 2.5, {halign: 'center'});
                                        
                                        this.setFont(doc, null, null, 'normal');
                                    }
                                }
                            }
                        }
                    }
                },
                didDrawPage: (HookData) => {
                    this.addHeader(HookData.doc);
                }
            });
            
            position_y += doc.lastAutoTable.finalY;
            
            return position_y;
        },
        generatePartialSummaryPage2(doc, position_y) {
            this.addDocPage(doc);
            this.addHeader(doc);
            position_y = this.firstElementSpacing;
            
            
            position_y = this.addTitle(doc, position_y, 1, this.$t('세부 항목별 평가결과 요약', this.report_lang));
            
            this.makeScoreLegend(doc, position_y);
            
            position_y += 26;
            
            const _category3_group_left = this.scoringInfo.category3.filter(({category1_question_code}) => this.mapCategory0QCode[category1_question_code].category === 's');
            const _category3_group_right = this.scoringInfo.category3.filter(({category1_question_code}) => this.mapCategory0QCode[category1_question_code].category === 'e');
            _category3_group_left.sort((a, b) => this.mapCategory0QCode[a.category1_question_code].order - this.mapCategory0QCode[b.category1_question_code].order);
            _category3_group_right.sort((a, b) => this.mapCategory0QCode[a.category1_question_code].order - this.mapCategory0QCode[b.category1_question_code].order);
            
            
            const tableWidth = (this.pageContentWidth / 2) - 5;
            const _category3_table_left = this.jsPDFOpt.margin.left;
            const _category3_table_right = (this.pageContentWidth / 2) + 5 + this.jsPDFOpt.margin.left;
            
            this.setFont(doc, null, null, 'semibold');
            doc.setFontSize(13);
            doc.setTextColor('#292828');
            doc.text(this.$t('사회', this.report_lang), _category3_table_left, position_y);
            doc.text(this.$t('환경', this.report_lang), _category3_table_right, position_y);
            doc.setLineWidth(0.5);
            doc.setDrawColor('#555555');
            doc.line(_category3_table_left, position_y+6, _category3_table_left+tableWidth, position_y+6);
            doc.line(_category3_table_right, position_y+6, _category3_table_right+tableWidth, position_y+6);
            
            position_y += 14;
            
            
            let _category3_group_left_postion_y = position_y;
            let _category3_group_right_postion_y = position_y;
            
            
            for (const _category3 of _category3_group_left) {
                
                const {category1_question_name, category1_score} = _category3;
                let category1_question_name_score = `${category1_question_name}: ${category1_score}`;
                if (this.report_lang === 'ko') {
                    category1_question_name_score += '점';
                }
                if (this.report_lang === 'ca') {
                    category1_question_name_score += '分';
                }
                _category3_group_left_postion_y = this.addTitle(doc, _category3_group_left_postion_y, 3, category1_question_name_score, {
                    skip_divider: true,
                    no_margin: true,
                    position_x: _category3_table_left
                });
                
                const _category3_score_table_body = _category3.category3.map(({
                    category3_question_name,
                    score,
                    not_applicable
                }) => {return [
                    {
                        content: category3_question_name,
                        styles: {...this.tableStyles.td,}
                    },
                    {
                        content: not_applicable ? 'N/A' : score,
                        contentType: 'score',
                        styles: {...this.tableStyles.td,}
                    },
                ];
                });
                
                _category3_score_table_body.unshift([
                    {content: this.$t('중분류', this.report_lang), styles: {...this.tableStyles.th, cellWidth: tableWidth * 0.75}},
                    {content: this.$t('점수', this.report_lang), styles: {...this.tableStyles.th, cellWidth: tableWidth * 0.25}},
                ]);
                
                autoTable(doc, {
                    theme: 'grid',
                    startY: _category3_group_left_postion_y,
                    tableWidth: tableWidth,
                    rowPageBreak: 'auto',
                    margin: {...this.jsPDFOpt.margin, top: 0, left: _category3_table_left},
                    styles: {...this.tableStyles.global, halign: 'center'},
                    body: _category3_score_table_body,
                    willDrawCell: (data) => {
                        if (data.section === 'body') {
                            const {cell} = data;
                            
                            if (cell.raw?.contentType === 'score' && cell.raw?.content != null) {
                                cell.text = [];
                            }
                        }
                    },
                    didDrawCell: (data) => {
                        const { cell, row, column, section } = data;
                        const center_x = cell.x + cell.width / 2; // 셀의 가로 중앙
                        
                        // body
                        if (section === 'body' && column.index > 0) {
                            const cellData = cell.raw;
                            if (cell.raw?.contentType === 'score' && cell.raw?.content != null) {
                                
                                const y_position = cell.y + cell.height / 2;
                                
                                if (cellData.content === 'N/A') {
                                    this.setFont(doc, null, null, 'normal');
                                    doc.setFontSize(9);
                                    doc.setTextColor('#292828');
                                    doc.text(cellData.content.toString(), center_x - 8, y_position + 2.5, {halign: 'center'});
                                } else {
                                    this.setFont(doc, null, null, 'bold');
                                    doc.setFontSize(10);
                                    doc.setTextColor('#292828');
                                    doc.setFillColor(getStateColor(cellData.content));
                                    doc.circle(center_x - 12, y_position, 2, 'F');
                                    
                                    
                                    doc.text(cellData.content.toString(), center_x - 4, y_position + 2.5, {halign: 'center'});
                                    
                                    this.setFont(doc, null, null, 'normal');
                                }
                            }
                        }
                    },
                    didDrawPage: (HookData) => {
                        this.addHeader(HookData.doc);
                    }
                });
                
                _category3_group_left_postion_y = doc.lastAutoTable.finalY + 4;
            }
            
            for (const _category3 of _category3_group_right) {
                const {category1_question_name, category1_score} = _category3;
                let category1_question_name_score = `${category1_question_name}: ${category1_score}`;
                if (this.report_lang === 'ko') {
                    category1_question_name_score += '점';
                }
                if (this.report_lang === 'ca') {
                    category1_question_name_score += '分';
                }
                _category3_group_right_postion_y = this.addTitle(doc, _category3_group_right_postion_y, 3, category1_question_name_score, {
                    skip_divider: true,
                    no_margin: true,
                    position_x: _category3_table_right
                });
                
                const _category3_score_table_body = _category3.category3.map(({
                    category3_question_name,
                    score,
                    not_applicable
                }) => {
                    return [
                        {
                            content: category3_question_name,
                            styles: {...this.tableStyles.td,}
                        },
                        {
                            content: not_applicable ? 'N/A' : score,
                            contentType: 'score',
                            styles: {...this.tableStyles.td,}
                        },
                    ];
                });
                
                _category3_score_table_body.unshift([
                    {content: this.$t('중분류', this.report_lang), styles: {...this.tableStyles.th, cellWidth: tableWidth * 0.75}},
                    {content: this.$t('점수', this.report_lang), styles: {...this.tableStyles.th, cellWidth: tableWidth * 0.25}},
                ]);
                
                autoTable(doc, {
                    theme: 'grid',
                    startY: _category3_group_right_postion_y,
                    tableWidth: (this.pageContentWidth / 2) - 5,
                    rowPageBreak: 'auto',
                    margin: {...this.jsPDFOpt.margin, top: 0, left: _category3_table_right},
                    styles: {...this.tableStyles.global, halign: 'center'},
                    body: _category3_score_table_body,
                    willDrawCell: (data) => {
                        if (data.section === 'body') {
                            const {cell} = data;
                            
                            if (cell.raw?.contentType === 'score' && cell.raw?.content != null) {
                                cell.text = [];
                            }
                        }
                    },
                    didDrawCell: (data) => {
                        const { cell, row, column, section } = data;
                        const center_x = cell.x + cell.width / 2; // 셀의 가로 중앙
                        
                        // body
                        if (section === 'body' && column.index > 0) {
                            const cellData = cell.raw;
                            if (cell.raw?.contentType === 'score' && cell.raw?.content != null) {
                                
                                const y_position = cell.y + cell.height / 2;
                                
                                if (cellData.content === 'N/A') {
                                    this.setFont(doc, null, null, 'normal');
                                    doc.setFontSize(9);
                                    doc.setTextColor('#292828');
                                    doc.text(cellData.content.toString(), center_x - 8, y_position + 2.5, {halign: 'center'});
                                } else {
                                    this.setFont(doc, null, null, 'bold');
                                    doc.setFontSize(10);
                                    doc.setTextColor('#292828');
                                    doc.setFillColor(getStateColor(cellData.content));
                                    doc.circle(center_x - 12, y_position, 2, 'F');
                                    
                                  
                                    doc.text(cellData.content.toString(), center_x - 4, y_position + 2.5, {halign: 'center'});
                                    
                                    this.setFont(doc, null, null, 'normal');
                                }
                            }
                        }
                    },
                    didDrawPage: (HookData) => {
                        this.addHeader(HookData.doc);
                    }
                });
                
                _category3_group_right_postion_y = doc.lastAutoTable.finalY + 4;
            }
            
            position_y = _category3_group_left_postion_y > _category3_group_right_postion_y ? _category3_group_left_postion_y : _category3_group_right_postion_y;
            return position_y;
        },
        async generateCategory3AnalysisPage(doc, position_y) {
            this.addDocPage(doc);
            this.addHeader(doc, 2);
            position_y = this.firstElementSpacing;
            
            // 세부 영역별 평가 결과
            position_y = this.addTitle(doc, position_y, 1, '세부 영역별 평가 결과');
            
            // 세부 영역별 점수
            for (const [category3_idx, _category3] of this.scoringInfo.category3.entries()) {
                if (category3_idx > 0) {
                    this.addDocPage(doc);
                    this.addHeader(doc, 2);
                    position_y = this.firstElementSpacing;
                }
                
                const {category1_question_code, category1_question_name} = _category3;
                
                position_y = this.addTitle(doc, position_y, 2, category1_question_name, {
                    skip_divider: category3_idx === 0,
                    no_padding: category3_idx === 0,
                });
                // { skip_divider: true }
                position_y -= 5;
                position_y = this.addTitle(doc, position_y, 3, '영역별 평가 결과', {
                    skip_divider: true,
                    no_margin: true,
                    no_padding: true
                });
                
                const _category3_score_box_padding = 5;
                const _category1_score_box_width = this.pageContentWidth;
                const _category1_score_box_half_position_y = this.jsPDFOpt.margin.left + ((this.pageContentWidth / 2) - _category3_score_box_padding);
                const _category1_score_box_half_width = ((this.pageContentWidth / 2) - _category3_score_box_padding);
                let _category1_score_box_height = this.report_lang === 'en' ? 100 : 85; // 90 // 100
                if(category1_question_name.includes('Supply Chain')){
                    _category1_score_box_height += 20;
                }
                const _category1_score_box_inner_position_y_begin = position_y + _category3_score_box_padding;
                const _category1_score_box_inner_position_y_end = _category1_score_box_inner_position_y_begin + _category1_score_box_height - (_category3_score_box_padding * 2);
                
                doc.setFillColor(...this.jsPDFOpt.card.fillColor);
                doc.setLineWidth(this.jsPDFOpt.card.lineWidth * 2);
                doc.setDrawColor(this.jsPDFOpt.card.borderColor);
                doc.rect(this.jsPDFOpt.margin.left, position_y, _category1_score_box_width, _category1_score_box_height, 'FD');
                
                doc.setLineWidth(0.75);
                doc.setDrawColor('#E0E0E0');
                doc.line(_category1_score_box_half_position_y, _category1_score_box_inner_position_y_begin, _category1_score_box_half_position_y, _category1_score_box_inner_position_y_end);
                
                const _category3_category1_question_name = doc.splitTextToSize(`${_category3.category1_question_name} ${this.$t('총점', this.report_lang)}`, _category1_score_box_half_width - 10);
                const _category3_category1_question_name_dim = doc.getTextDimensions(_category3_category1_question_name);
                const _category3_category1_question_name_position_x = this.jsPDFOpt.margin.left + 10;
                const _category3_category1_question_name_position_y = _category1_score_box_inner_position_y_begin + (_category3_score_box_padding * 2);
                doc.text(_category3_category1_question_name, _category3_category1_question_name_position_x, _category3_category1_question_name_position_y);
                const _category3_category1_question_name_position_y_end = _category3_category1_question_name_position_y + _category3_category1_question_name_dim.h;
                
                doc.setFontSize(30);
                let _category3_category1_question_score = this.report_lang === 'ko' ? `${_category3.category1_score} 점` : `${_category3.category1_score}`;
                _category3_category1_question_score = doc.splitTextToSize(_category3_category1_question_score, _category1_score_box_half_width);
                const _category3_category1_question_score_dim = doc.getTextDimensions(_category3_category1_question_score);
                const _category3_category1_question_score_position_x = this.jsPDFOpt.margin.left + ((_category1_score_box_half_width - _category3_category1_question_score_dim.w) / 2);
                const _category3_category1_question_score_position_y = _category3_category1_question_name_position_y_end + ((_category1_score_box_inner_position_y_end - _category3_category1_question_name_position_y_end) / 2);
                doc.text(_category3_category1_question_score, _category3_category1_question_score_position_x, _category3_category1_question_score_position_y);
                
                
                this.setFont(doc, 'table_td', null);
                doc.setFontSize(10.5);
                const _category1_description_padding = 10;
                const _category1_description_text = doc.splitTextToSize(this.category1_description[category1_question_code], (this.pageContentWidth / 2) - (_category1_description_padding * 2));
                const _category1_description_text_dim = doc.getTextDimensions(_category1_description_text);
                const _category1_description_position_y = (_category1_score_box_height - _category1_description_text_dim.h) / 2 + (_category1_description_padding / 2) + (this.report_lang === 'en' ? -2 : 0);
                doc.text(_category1_description_text, _category1_score_box_half_position_y + _category1_description_padding, position_y + _category1_description_position_y, {lineHeightFactor: 1.3});
                
                position_y += _category1_score_box_height + this.jsPDFOpt.spacing.section_xs - 5;
                
                if (this.checkNeedNewPage(doc, position_y, 100)) {
                    this.addDocPage(doc, true);
                    position_y = this.firstElementSpacing;
                }
                
                position_y = this.addTitle(doc, position_y, 3, this.$t('세부 문항별 평가결과', this.report_lang), {
                    skip_divider: true,
                    no_margin: true,
                    footnote: this.report_lang === 'ko' ? '중분류 점수는 100 점 만점 기준 환산 값' : null
                });
                
                
                // 차트 영역
                const _filtered_category3_score = _category3.category3.filter(({not_applicable}) => not_applicable === false);
                
                const _category3_chart_area_position_y = position_y;
                const _category3_chart_area_width = this.pageContentWidth;
                const _category3_chart_area_height = ((_filtered_category3_score.length || 1) * 20) + 10;
                doc.setFillColor('#fff');
                doc.setLineWidth(this.jsPDFOpt.card.lineWidth);
                doc.setDrawColor(this.jsPDFOpt.card.borderColor);
                doc.rect(this.jsPDFOpt.margin.left, _category3_chart_area_position_y, _category3_chart_area_width, _category3_chart_area_height, 'FD');
                
                // 차트 변수
                const max_text_width = Math.max(..._filtered_category3_score.map((item) => {
                    return doc.getTextDimensions(doc.splitTextToSize(item.category3_question_name), {fontSize: 9.5}).w;
                }));
                const _category3_chart_box_padding = 5;
                const _category3_chart_area_position_x = this.jsPDFOpt.margin.left;
                const _cat3_label_position_x = _category3_chart_area_position_x + max_text_width + _category3_chart_box_padding + 10;
                
                const _category3_chart_y_axis_position_x = _cat3_label_position_x + _category3_chart_box_padding;
                let _category3_chart_y_axis_position_y_end = 0;
                const _category3_chart_bar_width = this.pageContentWidth - _cat3_label_position_x;
                
                _filtered_category3_score.forEach((category3, idx) => {
                    // category1 라벨
                    const _cat3_label_position_y = position_y + (idx * 20) + _category3_chart_box_padding + 12;
                    const data_label = category3.not_applicable ? 0 : category3.score;
                    
                    this.setFont(doc, null, null, 'medium');
                    doc.setFontSize(10);
                    doc.text(category3.category3_question_name, _cat3_label_position_x, _cat3_label_position_y, {align: 'right'});
                    _category3_chart_y_axis_position_y_end = _cat3_label_position_y;
                    
                    // category1 차트 바
                    const _category3_chart_bar_height = 9;
                    const _category3_chart_bar_position_y = _cat3_label_position_y - 7;
                    const _category3_chart_bar_position_x_end = _category3_chart_bar_width * (data_label / 100);
                    doc.setFillColor(...this.hyundai_colors_rgb.blue);
                    doc.rect(_category3_chart_y_axis_position_x, _category3_chart_bar_position_y, _category3_chart_bar_position_x_end, _category3_chart_bar_height, 'F');
                    
                    // _category3_chart_datalabel = doc.splitTextToSize(_category3_chart_datalabel);
                    const chart_datalabel_dim = doc.getTextDimensions(data_label.toString(), {fontSize: 7});
                    let chart_datalabel_position_x = 0;
                    
                    if (data_label > 15) {
                        chart_datalabel_position_x = (_category3_chart_bar_position_x_end - chart_datalabel_dim.w - 2) / 2;
                    } else {
                        chart_datalabel_position_x = _category3_chart_bar_position_x_end + 4;
                    }
                    //
                    // // Category3 차트 - 데이터라벨 추가
                    this.setFont(doc, null, null, 'medium');
                    if (data_label > 15) {
                        doc.setTextColor('#fff');
                    } else {
                        doc.setTextColor('#424242');
                    }
                    doc.setFontSize(8);
                    const chart_datalabel_position_y = data_label ? _category3_chart_bar_position_y + chart_datalabel_dim.h + 1.4 : _category3_chart_bar_position_y + 7;
                    doc.text(data_label.toString(), _category3_chart_y_axis_position_x + chart_datalabel_position_x, chart_datalabel_position_y);
                });
                
                doc.setLineWidth(0.5);
                doc.setDrawColor('#E0E0E0');
                
                if (_filtered_category3_score.length > 0) {
                    doc.line(_category3_chart_y_axis_position_x, _category3_chart_area_position_y + 6, _category3_chart_y_axis_position_x, _category3_chart_y_axis_position_y_end + 6);
                } else {
                    doc.setTextColor(this.hyundai_colors.muted);
                    doc.text(this.$t('영역 내 핵심지표가 없습니다.'), _category1_score_box_half_position_y, position_y + 18, {align: 'center'});
                }
                
                position_y += _category3_chart_area_height + this.jsPDFOpt.spacing.section_sm;
                
                
                if (this.checkNeedNewPage(doc, position_y, 30)) {
                    this.addDocPage(doc, true);
                    position_y = this.firstElementSpacing;
                } else {
                    // position_y -= 3
                }
                
                // 핵심지표 결과 테이블 범주
                const _fill_color = {
                    '충족': this.hyundai_colors.blue,
                    '일부항목 충족': '#fa7e14',
                    '미충족': '#dc353c',
                    '해당없음': '#BDBDBD'
                };
                let _category3_table_legend_ele = (this.pageContentWidth / 3);
                let legends_ko = ['충족', '일부항목 충족', '미충족', '해당없음'];
                let _longest_legend_position_x_weight = {
                    ko: 28,
                    en: 0,
                    ca: 40
                };
                let _longest_legend_position_x = this.jsPDFOpt.margin.left + (this.pageContentWidth / 2) + _longest_legend_position_x_weight[this.report_lang];
                let _legend_mark_width = 7;
                let _legend_mark_position_y = position_y + 1;
                Object.values(_fill_color).forEach((_fill_color_ele, _fill_color_idx) => {
                    this.setFont(doc, 'table_td', null, 'normal');
                    doc.setFontSize(9);
                    let _legend_name = 3 > _fill_color_idx
                        ? `${this.$t(legends_ko[_fill_color_idx], this.report_lang)}, `
                        : this.$t(legends_ko[_fill_color_idx], this.report_lang);
                    _legend_name = doc.splitTextToSize(_legend_name, _category3_table_legend_ele);
                    let _legend_name_dim = doc.getTextDimensions(_legend_name);
                    
                    doc.setFillColor(Object.values(_fill_color)[_fill_color_idx]);
                    doc.circle(_longest_legend_position_x, _legend_mark_position_y, 3, 'F');
                    
                    let _legend_name_position_x = _longest_legend_position_x + _legend_mark_width - 1;
                    doc.text(`: ${_legend_name}`, _legend_name_position_x, _legend_mark_position_y + 2);
                    _longest_legend_position_x += _legend_mark_width + _legend_name_dim.w + 5 + 5;
                });
                
                
                position_y = this.addTitle(doc, position_y - 5, 3, this.$t('핵심지표 결과', this.report_lang), {
                    skip_divider: true,
                    no_margin: true
                });
                let _category1_key_indicator = this.scoring?.category1_importance_pd
                    .filter(({category1_question_code}) => category1_question_code === _category3.category1_question_code
                    );
                _category1_key_indicator = _category1_key_indicator.reduce((_key_indicators, _key_indicator) => {
                    let {
                        category3_question_code,
                        category3_question_name,
                        category3_question_name_ca,
                        category3_question_name_ea,
                        sub_code,
                        level,
                        label
                    } = _key_indicator;
                    
                    let _category3_question_name = this.report_lang === 'ko'
                        ? category3_question_name
                        : this.report_lang === 'ca' ? category3_question_name_ca : category3_question_name_ea;
                    
                    
                    if (!_key_indicators[category3_question_code]) {
                        _key_indicators[category3_question_code] = {
                            category3_question_name: _category3_question_name,
                            count: 0,
                            indicators: []
                        };
                    }
                    
                    
                    _key_indicators[category3_question_code].count++;
                    _key_indicators[category3_question_code].indicators.push({
                        label,
                        content: this.importance_sub_msg?.[label]?.[this.report_lang] ,
                        level: !level ? 'N/A' : level
                    });
                    
                    return _key_indicators;
                }, {});
                _category1_key_indicator = Object.values(_category1_key_indicator);
                
                // 핵심지표 결과 테이블
                const category1_key_indicator_header = [
                    [
                        {content: this.$t('중분류', this.report_lang), styles: {cellWidth: this.pageContentWidth * 0.3}},
                        {content: this.$t('번호', this.report_lang), styles: {cellWidth: this.pageContentWidth * 0.1}},
                        {content: this.$t('내용', this.report_lang), styles: {cellWidth: this.pageContentWidth * 0.5}},
                        {content: this.$t('현황', this.report_lang), styles: {cellWidth: this.pageContentWidth * 0.1}},
                    ]
                ];
                const category1_key_indicators = _category1_key_indicator.reduce((_key_indicator_rows, _key_indicator_ele) => {
                    const {category3_question_name, count, indicators} = _key_indicator_ele;
                    indicators.forEach(({label, content, level}, indicators_idx) => {
                        const _indicator_style = {...this.tableStyles.td};
                        _indicator_style.textColor = level === null || level === 'N/A'
                            ? _fill_color['해당없음'] //'#E0E0E0'
                            : level === 'full'
                                ? _fill_color['충족'] : level === 'half' ? _fill_color['일부항목 충족'] : _fill_color['미충족'];
                        let _indicator_row = [
                            {content: label, styles: this.tableStyles.td},
                            {content: content, styles: {...this.tableStyles.td, halign: 'left'}},
                            {
                                content: level === null ? 'N/A' : '●',
                                styles: _indicator_style
                            },
                        ];
                        
                        if (indicators_idx < 1) {
                            _indicator_row.unshift({
                                content: category3_question_name, rowSpan: count, styles: this.tableStyles.td
                            });
                        }
                        
                        
                        _key_indicator_rows.push(_indicator_row);
                    });
                    
                    return _key_indicator_rows;
                }, []);
                
                if(category1_key_indicators.length === 0){
                    category1_key_indicators.push([
                        {content: this.$t('영역 내 핵심지표가 없습니다.', this.report_lang), colSpan: 4, styles: {...this.tableStyles.td, textColor: this.hyundai_colors.muted, halign: 'center', minCellHeight: 100 }}
                    ]);
                    
                }
                
                
                autoTable(doc, {
                    theme: 'grid',
                    startY: position_y,
                    tableWidth: this.pageContentWidth,
                    showHead: 'everyPage',
                    rowPageBreak: 'auto',
                    margin: {...this.jsPDFOpt.margin, top: this.firstElementSpacing, left: this.jsPDFOpt.margin.left},
                    styles: {...this.tableStyles.global, halign: 'center'},
                    headStyles: this.tableStyles.th,
                    head: category1_key_indicator_header,
                    body: category1_key_indicators,
                    didDrawPage: (HookData) => {
                        this.addHeader(HookData.doc);
                    }
                });
                
                // position_y = doc.lastAutoTable.finalY + this.jsPDFOpt.spacing.section_md
                // if (this.checkNeedNewPage(doc, position_y, 100)) {
                //     position_y = this.firstElementSpacing
                // }
            }
            
            if (this.checkNeedNewPage(doc, position_y, 100)) {
                this.addDocPage(doc, true);
                position_y = this.firstElementSpacing;
            }
            
            return position_y;
        },
        
        
        generateCategory1PieChartInstance(category1_score) {
            return new Promise((resolve) => {
                const _scope1_data = {
                    datasets: [{
                        data: category1_score,
                        fill: true,
                        backgroundColor: [
                            this.hyundai_colors.blue,
                            'rgba(236, 239, 241, 0.7)'
                            // 'rgba(228, 220, 211, 0.4)'
                        ],
                        borderColor: [
                            this.hyundai_colors.blue,
                            'rgba(144, 164, 174, 0.6)'
                        ],
                    }]
                };
                
                const _common_option = {
                    global: {
                        aspectRatio: 5 / 5,
                        responsive: false,
                        maintainAspectRatio: false,
                        responsiveAnimationDuration: 0,
                        animation: {
                            duration: 0,
                            onComplete: function () {
                                resolve(this.toBase64Image('image/jpg', 1));
                            }
                        }
                    },
                    plugins: {
                        datalabels: {display: false}
                    },
                };
                
                const _scope1_area_option = {
                    ..._common_option.global,
                    elements: {line: {borderWidth: 1}},
                    scales: {},
                    plugins: _common_option.plugins,
                    cutout: '77.5%'
                };
                
                if (!this.category1PieChartInstance) {
                    this.category1PieChartInstance = this.$chartjs.createChart(
                        document.getElementById('category1-chart-pie'),
                        {
                            type: 'doughnut',
                            data: _scope1_data,
                            options: _scope1_area_option
                        }
                    );
                } else {
                    this.$chartjs.updateChart(this.category1PieChartInstance, _scope1_data, _scope1_area_option);
                }
            });
            
            // return this.drawCategory1PieChartImage()
        },
        drawCategory1PieChartImage() {
            return this.category1PieChartInstance.toBase64Image('image/jpg', 1);
        },
        generatePAR_Category1AreaChartInstance(category1_label, category1_score, opt = {}) {
            let _category1_label = category1_label.map(question_name => this.$t(question_name, this.report_lang));
            if (this.report_lang === 'en') {
                _category1_label = this.splitByManagement(_category1_label);
            }
            
            if (!opt.emphasis) {
                opt.emphasis = false;
            }
            
            const _scope1_data = {
                labels: _category1_label,
                datasets: [{
                    label: this.headerInfo?.company_name,
                    data: category1_score,
                    fill: true,
                    backgroundColor: 'rgba(0, 105, 177, 0.4)',
                    borderColor: 'rgb(0, 105, 177)', // 'rgb(90, 162, 235)',
                    pointBackgroundColor: 'rgb(90, 162, 235)'
                }]
            };
            
            const _common_option = {
                global: {
                    aspectRatio: this.report_lang === 'en' ? 5 / 3 : 4.7 / 3,
                    responsive: false,
                    maintainAspectRatio: false,
                    responsiveAnimationDuration: 0,
                    animation: {
                        duration: 0,
                        // onComplete: function () {
                        //     resolve(this.toBase64Image('image/jpg', 1));
                        // }
                    }
                },
                plugins: {
                    legend: {display: false},
                    datalabels: {
                        display: false
                    }
                },
                scales: {
                    max: 100,
                    beginAtZero: true,
                    ticks: {
                        display: false
                    }
                }
            };
            
            const _scope1_area_option = {
                ..._common_option.global,
                layout: {
                    // padding: {
                    //     top: this.report_lang === 'en' ? 0 : 10,
                    //     right: 15,
                    //     bottom: this.report_lang === 'en' ? 0 : 10,
                    //     left: 15
                    // },
                },
                elements: {line: {borderWidth: 1.5}},
                scales: {
                    r: {
                        ..._common_option.scales,
                        ticks: {
                            ..._common_option.scales.ticks,
                            color: '#E0E0E0',
                            stepSize: 25
                        },
                        grid: {
                            color: '#E0E0E0'
                        },
                        pointLabels: {
                            display: true,
                            padding: 10,
                            font: {
                                family: 'Noto Sans KR',
                                size: (context) => {
                                    return context.label === opt.emphasis_label ? 16.5 : 14;
                                },
                                weight: (context) => {
                                    return context.label === opt.emphasis_label ? 'bold' : 'normal';
                                },
                            },
                            color: (context) => {
                                return context.label === opt.emphasis_label ? 'rgb(0, 105, 177)' : '#616161';
                            }
                        }
                        // pointLabels: {
                        //     display: true,
                        //     padding: 10,
                        //     font: {
                        //         family: "Noto Sans KR",
                        //         size: 11, // 13,
                        //         weight: 'normal',
                        //     },
                        //     color: "rgb(71, 71, 71)"
                        // }
                    }
                },
                plugins: _common_option.plugins
            };
            
            if (!this.PAR_Category1AreAChartInstance) {
                this.PAR_Category1AreAChartInstance = this.$chartjs.createChart(
                    document.getElementById('par-category1-chart-area'),
                    {
                        type: 'radar',
                        data: _scope1_data,
                        options: _scope1_area_option
                    }
                );
            } else {
                this.$chartjs.updateChart(this.PAR_Category1AreAChartInstance, _scope1_data, _scope1_area_option);
            }
            
            // if (this.PAR_Category1AreAChartInstance) {
            //     this.PAR_Category1AreAChartInstance.destroy()
            //     this.PAR_Category1AreAChartInstance = null
            // }
            
            return this.drawPAR_Category1AreaChartImage();
        },
        drawPAR_Category1AreaChartImage() {
            return this.PAR_Category1AreAChartInstance.toBase64Image('image/jpg', 1);
        },
        generatePACFPF_BarChartInstance() {
            const _par_score_set = this.scoring.par_scoring_pd.total_scoring_pd;
            const _category1_data = [
                this.roundToOneDecimalPlace(_par_score_set['A']['converted_score']),
                this.roundToOneDecimalPlace(_par_score_set['CF']['converted_score']),
                this.roundToOneDecimalPlace(_par_score_set['P']['converted_score']),
                this.roundToOneDecimalPlace(_par_score_set['PF']['converted_score'])
            ];
            const _scope1_data = {
                labels: [
                    this.$t('정책', this.report_lang),
                    this.$t('조치', this.report_lang),
                    this.$t('인증', this.report_lang),
                    this.$t('성과', this.report_lang),
                ],
                datasets: [{
                    label: this.headerInfo?.company_name,
                    data: _category1_data,
                    fill: true,
                    backgroundColor: this.hyundai_colors.blue,
                    borderColor: this.hyundai_colors.blue,
                    pointBackgroundColor: 'rgb(90, 162, 235)',
                    barPercentage: 1.5,
                    categoryPercentage: 0.4
                }]
            };
            
            const _common_option = {
                global: {
                    // aspectRatio: this.report_lang === 'en' ? 5/3 : 4.7/3,
                    aspectRatio: 4.7 / 3,
                    responsive: false,
                    maintainAspectRatio: false,
                    responsiveAnimationDuration: 0,
                    animation: {duration: 0}
                },
                plugins: {
                    legend: {display: false},
                    datalabels: {display: false}
                },
                scales: {
                    max: 100,
                    beginAtZero: true,
                    ticks: {
                        display: false
                    }
                }
            };
            
            const _scope1_bar_option = {
                ..._common_option.global,
                layout: {
                    padding: {
                        top: Math.max.apply(null, _category1_data) < 80 ? 10 : 34,
                        // top: this.report_lang === "en" ? 5 : Math.max.apply(null, _category1_data) < 80 ? 10 : 34,
                        right: 10,
                        bottom: Math.max.apply(null, _category1_data) < 80 ? 10 : 15,
                        // bottom: this.report_lang === "en" ? 5 : Math.max.apply(null, _category1_data) < 80 ? 10 : 15,
                        left: 10,
                    },
                },
                elements: {bar: {borderWidth: 1}},
                scales: {
                    x: {
                        max: 100,
                        grid: {
                            display: false,
                        },
                        ticks: {
                            stepSize: 25,
                            font: {
                                family: 'Noto Sans KR',
                                size: 13,
                                weight: 'normal',
                            },
                            color: 'rgb(71, 71, 71)'
                        }
                    },
                    y: {
                        ..._common_option.scales,
                        max: 100,
                        ticks: {
                            display: false,
                            font: {
                                family: 'Noto Sans KR',
                                padding: 0,
                                size: 10,
                                weight: 'normal',
                            },
                            color: 'rgb(71, 71, 71)',
                            padding: 0,
                            autoSkip: false
                        },
                        grid: {
                            borderWidth: 0,
                            display: false,
                            drawOnChartArea: false
                        }
                    }
                },
                plugins: {
                    ..._common_option.plugins,
                    datalabels: {
                        display: true,
                        align: 'end',
                        anchor: 'end',
                        color: '#343a40',
                        font: {
                            size: 12
                        }
                    }
                }
            };
            
            if (this.PAR_BarChartInstance) {
                this.PAR_BarChartInstance.destroy();
                this.PAR_BarChartInstance = null;
            }
            
            this.PAR_BarChartInstance = this.$chartjs.createChart(
                document.getElementById('par-chart-bar'),
                {
                    type: 'bar',
                    data: _scope1_data,
                    options: _scope1_bar_option
                }
            );
            
            return this.drawChartPARCategory1Image();
        },
        drawChartPARCategory1Image() {
            let _PAR_Category1BarChart_url = null;
            if (this.PAR_BarChartInstance) {
                _PAR_Category1BarChart_url = this.PAR_BarChartInstance.toBase64Image('image/png', 0.9);
            }
            
            return _PAR_Category1BarChart_url;
        },
        generateKeyFigurePage(doc, position_y) {
            position_y = this.firstElementSpacing;
            this.addDocPage(doc);
            this.addHeader(doc);
            
            // 데이터테이블 전역 footnote
            let _datatable_footnote = this.$t('위 데이터는 기업이 작성한 값으로, 값에 대한 증빙을 확인할 수 있는 경우 평가에 반영되었습니다.', this.report_lang);
            _datatable_footnote = doc.splitTextToSize(_datatable_footnote, this.pageContentWidth);
            const _datatable_footnote_dim = doc.getTextDimensions(_datatable_footnote);
            const _datatable_footnote_position_x = this.jsPDFOpt.margin.left + this.pageContentWidth;
            const _datatable_footnote_spacing_top = (doc) => {
                return doc.lastAutoTable.finalY + 10;
            };
            const _datatable_footnote_spacing_bottom = _datatable_footnote_dim.h + this.jsPDFOpt.spacing.section_xs; // this.jsPDFOpt.spacing.section_sm
            
            // Key Figures 섹션 타이틀
            position_y = this.addTitle(doc, position_y, 1, 'Key Figures');
            
            
            const _column_division_width = 80;
            const _column_unit_width = 30;
            // 주요 법규 위반 현황
            const _fill_color = {'위반사항 없음': this.hyundai_colors.blue, '위반사항 있음': '#dc353c'};
            let _category3_table_legend_ele = (this.pageContentWidth / 2);
            let legends_ko = ['위반사항 없음', '위반사항 있음'];
            let _longest_legend_position_x_weight = {
                ko: 85,
                en: 96,
                ca: 90
            };
            let _longest_legend_position_x = this.jsPDFOpt.margin.left + (this.pageContentWidth / 2) + _longest_legend_position_x_weight[this.report_lang];
            let _legend_mark_width = 7;
            let _legend_mark_position_y = position_y + 7;
            Object.values(_fill_color).forEach((_fill_color_ele, _fill_color_idx) => {
                this.setFont(doc, 'table_td', null, 'normal');
                doc.setFontSize(9);
                let _legend_name = 1 > _fill_color_idx
                    ? `${this.$t(legends_ko[_fill_color_idx], this.report_lang)}, `
                    : this.$t(legends_ko[_fill_color_idx], this.report_lang);
                _legend_name = doc.splitTextToSize(_legend_name, _category3_table_legend_ele);
                let _legend_name_dim = doc.getTextDimensions(_legend_name);
                
                doc.setFillColor(Object.values(_fill_color)[_fill_color_idx]);
                doc.circle(_longest_legend_position_x, _legend_mark_position_y, 3, 'F');
                
                let _legend_name_position_x = _longest_legend_position_x + _legend_mark_width - 1;
                doc.text(`: ${_legend_name}`, _legend_name_position_x, _legend_mark_position_y + 2);
                _longest_legend_position_x += _legend_mark_width + _legend_name_dim.w + 5 + 5;
            });
            
            this.setFont(doc, 'title_sm');
            position_y = this.addTitle(doc, position_y, 3, this.$t('주요법규 위반 현황', this.report_lang), {
                skip_divider: true,
                no_padding: true
            });
            
            const _violation_data = this.scoring.sub_scoring_pd.reduce((acc, cur) => {
                if (['Q1.2', 'Q3.2', 'Q4.2', 'Q5.2'].includes(cur.label)) {
                    acc[cur.label] = {
                        label: cur.label,
                        indicator_color: cur.score > 0 ? _fill_color['위반사항 없음'] : _fill_color['위반사항 있음']
                    };
                }
                return acc;
            }, {});
            
            const _initiative_table_content_column_width = (this.pageContentWidth - ((_column_division_width * 2) + (_column_unit_width * 2))) / 2;
            const _initiative_table_styles = {
                label: {
                    ...this.datatables.th,
                    cellWidth: _column_division_width,
                    valign: 'middle'
                },
                value: {
                    ...this.datatables.td,
                    fontSize: 9,
                    cellWidth: _initiative_table_content_column_width,
                    valign: 'middle'
                },
                indicator: {
                    ...this.datatables.td,
                    fontSize: 24,
                    cellWidth: _column_unit_width,
                    valign: 'middle'
                },
            };
            
            autoTable(doc, {
                theme: 'grid',
                startY: position_y,
                margin: {...this.jsPDFOpt.margin, top: 0},
                styles: this.datatables.global,
                body: [
                    [
                        {
                            content: this.$t('인권/노동법규 위반', this.report_lang),
                            styles: _initiative_table_styles.label
                        },
                        {
                            content: 'Q1.2',
                            styles: _initiative_table_styles.value
                        },
                        {
                            content: '•',
                            styles: {
                                ..._initiative_table_styles.indicator,
                                textColor: _violation_data['Q1.2']?.indicator_color
                            }
                        },
                        {
                            content: this.$t('윤리법규 위반', this.report_lang),
                            styles: _initiative_table_styles.label
                        },
                        {
                            content: 'Q3.2',
                            styles: _initiative_table_styles.value
                        },
                        {
                            content: '•',
                            styles: {
                                ..._initiative_table_styles.indicator,
                                textColor: _violation_data['Q3.2']?.indicator_color
                            }
                        },
                    ],
                    [
                        {
                            content: this.$t('안전법규 위반', this.report_lang),
                            styles: _initiative_table_styles.label
                        },
                        {
                            content: 'Q4.2',
                            styles: _initiative_table_styles.value
                        },
                        {
                            content: '•',
                            styles: {
                                ..._initiative_table_styles.indicator,
                                textColor: _violation_data['Q4.2']?.indicator_color
                            }
                        },
                        {
                            content: this.$t('환경법규 위반', this.report_lang),
                            styles: _initiative_table_styles.label
                        },
                        {
                            content: 'Q5.2',
                            styles: _initiative_table_styles.value
                        },
                        {
                            content: '•',
                            styles: {
                                ..._initiative_table_styles.indicator,
                                textColor: _violation_data['Q5.2']?.indicator_color
                            }
                        },
                    ],
                ],
                didDrawCell: (data) => this.jspdf_common_draw_cell(data, {headLineColor: this.hyundai_colors_rgb.blue})
            });
            
            position_y = _datatable_footnote_spacing_top(doc);
            
            
            // 온실가스 배출량
            position_y = this.addTitle(doc, position_y, 3, this.$t('온실가스 배출량', this.report_lang), {
                skip_divider: true,
                no_padding: true
            });
            
            
            let _greengas_unit = !this.keyFigureInfo_greengas['3년간 온실가스 간접 배출량(Scope 1)'] && !this.keyFigureInfo_greengas['3년간 온실가스 간접 배출량(Scope 1)']
                ? 'tCO2e'
                : this.keyFigureInfo_greengas['3년간 온실가스 간접 배출량(Scope 1)'].unit || this.keyFigureInfo_greengas['3년간 온실가스 간접 배출량(Scope 2)'].unit;
            
            const _greengas_data = {
                Scope1: !this.keyFigureInfo_greengas['3년간 온실가스 직접 배출량(Scope 1)']
                    ? {'FY2022': null, 'FY2023': null, 'FY2024': null}
                    : this.keyFigureInfo_greengas['3년간 온실가스 직접 배출량(Scope 1)'].value,
                Scope2: !this.keyFigureInfo_greengas['3년간 온실가스 간접 배출량(Scope 2)']
                    ? {'FY2022': null, 'FY2023': null, 'FY2024': null}
                    : this.keyFigureInfo_greengas['3년간 온실가스 간접 배출량(Scope 2)'].value
            };
            
            autoTable(doc, {
                theme: 'grid',
                startY: position_y,
                margin: {...this.jsPDFOpt.margin, top: 0, left: this.jsPDFOpt.margin.left},
                styles: this.datatables.global,
                body: [
                    [
                        {content: this.$t('구분', this.report_lang), styles: this.datatables.th},
                        {
                            content: this.$t('단위', this.report_lang),
                            styles: {...this.datatables.th, cellWidth: _column_unit_width}
                        },
                        {content: this.$t('FY2022', this.report_lang), styles: this.datatables.th},
                        {content: this.$t('FY2023', this.report_lang), styles: this.datatables.th},
                        {content: this.$t('FY2024', this.report_lang), styles: this.datatables.th}
                    ],
                    [
                        {content: 'Scope 1', styles: {...this.datatables.th, cellWidth: _column_division_width / 2}},
                        {
                            content: _greengas_unit,
                            rowSpan: 2,
                            styles: {...this.datatables.th, cellWidth: _column_division_width / 2,}
                        },
                        {
                            content: this.formatNumber(_greengas_data['Scope1']['FY2022']) ?? ' - ',
                            styles: this.datatables.td
                        },
                        {
                            content: this.formatNumber(_greengas_data['Scope1']['FY2023']) ?? ' - ',
                            styles: this.datatables.td
                        },
                        {
                            content: this.formatNumber(_greengas_data['Scope1']['FY2024']) ?? ' - ',
                            styles: this.datatables.td
                        },
                    ],
                    [
                        {content: 'Scope 2', rowSpan: 1, styles: {...this.datatables.th}},
                        {
                            content: this.formatNumber(_greengas_data['Scope2']['FY2022']) ?? ' - ',
                            styles: this.datatables.td
                        },
                        {
                            content: this.formatNumber(_greengas_data['Scope2']['FY2023']) ?? ' - ',
                            styles: this.datatables.td
                        },
                        {
                            content: this.formatNumber(_greengas_data['Scope2']['FY2024']) ?? ' - ',
                            styles: this.datatables.td
                        },
                    ],
                ],
                didDrawCell: (data) => this.jspdf_common_draw_cell(data, {headLineColor: this.hyundai_colors_rgb.blue})
            });
            
            position_y = _datatable_footnote_spacing_top(doc);
            this.setFont(doc, 'footnote');
            doc.text(_datatable_footnote, _datatable_footnote_position_x, position_y, {align: 'right'});
            position_y += _datatable_footnote_spacing_bottom;
            
            
            // 근로손실 재해율
            if (this.report_lang === 'en') {
                position_y -= _datatable_footnote_dim.h / 2;
            }
            position_y = this.addTitle(doc, position_y, 3, this.$t('근로손실 재해율', this.report_lang), {
                skip_divider: true,
                no_padding: true
            });
            const ltifr_data = !this.keyFigureInfo_LTIFR['근로손실재해율(LTIFR)']
                ? {'2022': ' - ', '2023': ' - ', '2024': ' - '}
                : this.keyFigureInfo_LTIFR['근로손실재해율(LTIFR)'];
            
            autoTable(doc, {
                theme: 'grid',
                startY: position_y,
                margin: {...this.jsPDFOpt.margin, top: 0, left: this.jsPDFOpt.margin.left},
                styles: this.datatables.global,
                body: [
                    [
                        {
                            content: this.$t('구분', this.report_lang),
                            styles: {...this.datatables.th, cellWidth: _column_division_width}
                        },
                        {content: 'FY2022', styles: this.datatables.th},
                        {content: 'FY2023', styles: this.datatables.th},
                        {content: 'FY2024', styles: this.datatables.th},
                    ],
                    [
                        {content: 'LTIFR', styles: this.datatables.th},
                        {content: this.formatNumber(ltifr_data['2022']) ?? ' - ', styles: this.datatables.td},
                        {content: this.formatNumber(ltifr_data['2023']) ?? ' - ', styles: this.datatables.td},
                        {content: this.formatNumber(ltifr_data['2024']) ?? ' - ', styles: this.datatables.td},
                    ]
                ],
                didDrawCell: (data) => this.jspdf_common_draw_cell(data, {headLineColor: this.hyundai_colors_rgb.blue})
            });
            
            position_y = _datatable_footnote_spacing_top(doc);
            this.setFont(doc, 'footnote');
            doc.text(_datatable_footnote, _datatable_footnote_position_x, position_y, {align: 'right'});
            position_y += _datatable_footnote_spacing_bottom;
            
            if (this.checkNeedNewPage(doc, position_y)) {
                this.addDocPage(doc, true);
                position_y = this.firstElementSpacing;
            }
            
            // 환경 데이터
            if (this.report_lang === 'en') {
                position_y -= _datatable_footnote_dim.h / 2;
            }
            position_y = this.addTitle(doc, position_y, 3, this.$t('환경 데이터', this.report_lang), {
                skip_divider: true,
                no_padding: true
            });
            
            
            autoTable(doc, {
                theme: 'grid',
                startY: position_y,
                margin: {...this.jsPDFOpt.margin, top: 0, left: this.jsPDFOpt.margin.left},
                styles: this.datatables.global,
                body: [
                    [
                        {
                            content: this.$t('구분', this.report_lang),
                            styles: {...this.datatables.th, cellWidth: _column_division_width}
                        },
                        {
                            content: this.$t('단위', this.report_lang),
                            styles: {...this.datatables.th, cellWidth: 30, halign: 'center'}
                        },
                        {content: 'FY2022', styles: this.datatables.th},
                        {content: 'FY2023', styles: this.datatables.th},
                        {content: 'FY2024', styles: this.datatables.th}
                    ],
                    [
                        {content: this.$t('에너지 사용량', this.report_lang), styles: this.datatables.th},
                        {content: this.keyFigureInfo_environment['에너지 사용량']?.unit || 'MWh', styles: this.datatables.td},
                        {
                            content: this.formatNumber(this.keyFigureInfo_environment['에너지 사용량']?.value['FY2022']) ?? ' - ',
                            styles: this.datatables.td
                        },
                        {
                            content: this.formatNumber(this.keyFigureInfo_environment['에너지 사용량']?.value['FY2023']) ?? ' - ',
                            styles: this.datatables.td
                        },
                        {
                            content: this.formatNumber(this.keyFigureInfo_environment['에너지 사용량']?.value['FY2024']) ?? ' - ',
                            styles: this.datatables.td
                        },
                    ],
                    [
                        {content: this.$t('일반 폐기물 배출량', this.report_lang), styles: this.datatables.th},
                        {
                            content: this.keyFigureInfo_environment['일반 폐기물 배출량']?.unit ?? 'ton',
                            styles: this.datatables.td
                        },
                        {
                            content: this.formatNumber(this.keyFigureInfo_environment['일반 폐기물 배출량']?.value['FY2022']) ?? ' - ',
                            styles: this.datatables.td
                        },
                        {
                            content: this.formatNumber(this.keyFigureInfo_environment['일반 폐기물 배출량']?.value['FY2023']) ?? ' - ',
                            styles: this.datatables.td
                        },
                        {
                            content: this.formatNumber(this.keyFigureInfo_environment['일반 폐기물 배출량']?.value['FY2024']) ?? ' - ',
                            styles: this.datatables.td
                        },
                    ],
                    [
                        {content: this.$t('유해 폐기물 배출량', this.report_lang), styles: this.datatables.th},
                        {
                            content: this.keyFigureInfo_environment['유해 폐기물 배출량']?.unit ?? 'ton',
                            styles: this.datatables.td
                        },
                        {
                            content: this.formatNumber(this.keyFigureInfo_environment['유해 폐기물 배출량']?.value['FY2022']) ?? ' - ',
                            styles: this.datatables.td
                        },
                        {
                            content: this.formatNumber(this.keyFigureInfo_environment['유해 폐기물 배출량']?.value['FY2023']) ?? ' - ',
                            styles: this.datatables.td
                        },
                        {
                            content: this.formatNumber(this.keyFigureInfo_environment['유해 폐기물 배출량']?.value['FY2024']) ?? ' - ',
                            styles: this.datatables.td
                        },
                    ],
                    [
                        {content: this.$t('용수 취수량', this.report_lang), styles: this.datatables.th},
                        {content: this.keyFigureInfo_environment['용수 취수량']?.unit ?? 'ton', styles: this.datatables.td},
                        {
                            content: this.formatNumber(this.keyFigureInfo_environment['용수 취수량']?.value['FY2022']) ?? ' - ',
                            styles: this.datatables.td
                        },
                        {
                            content: this.formatNumber(this.keyFigureInfo_environment['용수 취수량']?.value['FY2023']) ?? ' - ',
                            styles: this.datatables.td
                        },
                        {
                            content: this.formatNumber(this.keyFigureInfo_environment['용수 취수량']?.value['FY2024']) ?? ' - ',
                            styles: this.datatables.td
                        },
                    ],
                    [
                        {content: 'NOx', styles: this.datatables.th},
                        {
                            content: this.keyFigureInfo_environment['NOx(질소산화물) 배출량']?.unit ?? 'ton',
                            styles: this.datatables.td
                        },
                        {
                            content: this.formatNumber(this.keyFigureInfo_environment['NOx(질소산화물) 배출량']?.value['FY2022']) ?? ' - ',
                            styles: this.datatables.td
                        },
                        {
                            content: this.formatNumber(this.keyFigureInfo_environment['NOx(질소산화물) 배출량']?.value['FY2023']) ?? ' - ',
                            styles: this.datatables.td
                        },
                        {
                            content: this.formatNumber(this.keyFigureInfo_environment['NOx(질소산화물) 배출량']?.value['FY2024']) ?? ' - ',
                            styles: this.datatables.td
                        },
                    ],
                    [
                        {content: 'SOx', styles: this.datatables.th},
                        {
                            content: this.keyFigureInfo_environment['SOx(황산화물) 배출량']?.unit ?? 'ton',
                            styles: this.datatables.td
                        },
                        {
                            content: this.formatNumber(this.keyFigureInfo_environment['SOx(황산화물) 배출량']?.value['FY2022']) ?? ' - ',
                            styles: this.datatables.td
                        },
                        {
                            content: this.formatNumber(this.keyFigureInfo_environment['SOx(황산화물) 배출량']?.value['FY2023']) ?? ' - ',
                            styles: this.datatables.td
                        },
                        {
                            content: this.formatNumber(this.keyFigureInfo_environment['SOx(황산화물) 배출량']?.value['FY2024']) ?? ' - ',
                            styles: this.datatables.td
                        },
                    ],
                    [
                        {content: this.$t('먼지', this.report_lang), styles: this.datatables.th},
                        {
                            content: this.keyFigureInfo_environment['먼지(PM) 배출량']?.unit ?? 'ton',
                            styles: this.datatables.td
                        },
                        {
                            content: this.formatNumber(this.keyFigureInfo_environment['먼지(PM) 배출량']?.value['FY2022']) ?? ' - ',
                            styles: this.datatables.td
                        },
                        {
                            content: this.formatNumber(this.keyFigureInfo_environment['먼지(PM) 배출량']?.value['FY2023']) ?? ' - ',
                            styles: this.datatables.td
                        },
                        {
                            content: this.formatNumber(this.keyFigureInfo_environment['먼지(PM) 배출량']?.value['FY2024']) ?? ' - ',
                            styles: this.datatables.td
                        },
                    ]
                ],
                didDrawCell: (data) => this.jspdf_common_draw_cell(data, {headLineColor: this.hyundai_colors_rgb.blue})
            });
            
            position_y = _datatable_footnote_spacing_top(doc);
            this.setFont(doc, 'footnote');
            doc.text(_datatable_footnote, _datatable_footnote_position_x, position_y, {align: 'right'});
            position_y += _datatable_footnote_spacing_bottom;
            
            return position_y;
        },
        generateFactorsAndAuditData() {
            const auditData = {};
            // 인증 관련 조건 처리 예외 (2025-10-20)
            const exceptionSubList = ['Q1.8v2', 'Q1.16v2', 'Q3.7v2', 'Q4.3v2'];
            
            if (!this.reportData?.scoring?.scoring_pd || !this.reportData.scoring.scoring_pd.length) {
                this.factors = [];
                this.auditData = {};
            } else {
                const _scoring_pd = [...this.reportData.scoring.scoring_pd].sort((a, b) => {
                    // First, compare the question field
                    const subCodeComparison = a.label.localeCompare(b.label, undefined, {numeric: true});
                    if (subCodeComparison !== 0) {
                        return subCodeComparison;
                    }
                    
                    return a.satisfied && !b.satisfied
                        ? -1
                        : !a.satisfied && b.satisfied
                            ? 1
                            : 0;
                });
                
                const _lang = this.report_lang;
                const mapSubCodeToScoringPd = {};
                let ev_idx = 1;
                let last_sub_code = null;
                let _factors_raw = _scoring_pd.reduce((_factors_v2, _factor) => {
                    mapSubCodeToScoringPd[_factor.sub_code] = _factor;
                    if (!_factor.not_applicable && _factor.sub_code) {
                        
                        let {
                            category1_question_code,
                            category1_question_name,
                            category1_question_name_ea,
                            category1_question_name_ca,
                            category3_question_code,
                            category3_question_name,
                            category3_question_name_ea,
                            category3_question_name_ca,
                            sub_code,
                            sub_name,
                            sub_name_ca,
                            sub_name_ea,
                            satisfied,
                            ev_id,
                            ev_name,
                            ev_name_e,
                            ev_name_c,
                            ev_reverse_name,
                            ev_reverse_name_e,
                            ev_reverse_name_c,
                            ev_importance,
                            label,
                            audit_category_code,
                            review_comment,
                            review_comment_ca,
                            review_comment_ea,
                            reviewer_name,
                            audit_comment,
                            audit_comment_ea,
                            audit_comment_ca
                        } = _factor;
                        
                        let _sub_name = null;
                        _sub_name = _lang === 'ko' ? sub_name : _lang === 'en' ? sub_name_ea : sub_name_ca;
                        if (!_sub_name) _sub_name = null;
                        const _category3_question_name = _lang === 'ko' ? category3_question_name : _lang === 'en' ? category3_question_name_ea : category3_question_name_ca;
                        
                        //audits
                        if (audit_category_code != null && audit_category_code !== '') {
                            if (sub_code !== last_sub_code) {
                                ev_idx = 1;
                                last_sub_code = sub_code;
                            } else {
                                ev_idx++;
                            }
                            if (auditData[sub_code] == null) {
                                auditData[sub_code] = {};
                            }
                            auditData[sub_code][ev_id] = {
                                category3_question_name: _category3_question_name,
                                label,
                                review_comment,
                                review_comment_ca,
                                review_comment_ea,
                                reviewer_name,
                                audit_category_code,
                                audit_comment,
                                audit_comment_ea,
                                audit_comment_ca,
                                ev_idx,
                                sub_name: _sub_name
                            };
                        }
                        
                        // factors
                        let _category1_idx = _factors_v2.findIndex(_factors => _factors.category1_question_code === category1_question_code);
                        const _category1_question_name = _lang === 'ko' ? category1_question_name : _lang === 'en' ? category1_question_name_ea : category1_question_name_ca;
                        if (_category1_idx < 0) {
                            _factors_v2.push({
                                category1_question_code,
                                category1_question_name: _category1_question_name,
                                category3: []
                            });
                            
                            _category1_idx = _factors_v2.findIndex(_factors => _factors.category1_question_code === category1_question_code);
                        }
                        
                        let _category3_idex = _factors_v2[_category1_idx].category3.findIndex(_factors_category3 => _factors_category3.category3_question_code === category3_question_code);
                        if (_category3_idex < 0) {
                            _factors_v2[_category1_idx].category3.push({
                                category3_question_code, category3_question_name: _category3_question_name,
                                subs: []
                            });
                            _category3_idex = _factors_v2[_category1_idx].category3.findIndex(_factors_category3 => _factors_category3.category3_question_code === category3_question_code);
                        }
                        
                        let _content_ev = null;
                        if (satisfied) {
                            _content_ev = _lang === 'ko' ? ev_name : _lang === 'en' ? ev_name_e : ev_name_c;
                        } else {
                            _content_ev = _lang === 'ko' ? ev_reverse_name : _lang === 'en' ? ev_reverse_name_e : ev_reverse_name_c;
                        }
                        
                        if (!_content_ev) _content_ev = null;
                        
                        
                        let _sub_index = _factors_v2[_category1_idx].category3[_category3_idex].subs.findIndex(({sub_code: _sub_code}) => _sub_code === sub_code);
                        if (_sub_index === -1) {
                            _factors_v2[_category1_idx].category3[_category3_idex].subs.push({
                                sub_code,
                                label,
                                sub_name: _sub_name,
                                evaluations: [],
                                sub_review_comment: null // _content_review_comment
                            });
                            
                            _sub_index = _factors_v2[_category1_idx].category3[_category3_idex].subs.findIndex(({sub_code: _sub_code}) => _sub_code === sub_code);
                        }
                        
                        if (_content_ev && /^[^\p{L}\p{N}]+$/u.test(_content_ev) === false) {
                            let _evaluation_content = [];
                            const _row_1st = [{
                                content: satisfied ? this.$t('충족', this.report_lang) : this.$t('미충족', this.report_lang),
                                contentType: 'satisfied',
                                // rowSpan: !_content_audit_comment ? 1 : 2,
                                rowSpan: 1,
                                status: satisfied,
                                ev_id: ev_id,
                                styles: {}
                            }];
                            if (ev_importance === 'HIGH') {
                                _row_1st.push({
                                    content: this.$t('중요사항', this.report_lang),
                                    contentType: 'importance',
                                    styles: {halign: 'center', valign: 'middle', fillColor: 248}
                                });
                            }
                            _row_1st.push({
                                content: _content_ev,
                                contentType: 'content',
                                styles: this.factorstables.td
                            });
                            
                            const _has_audit_data = auditData[sub_code]?.[ev_id] != null;
                            _row_1st.push({
                                content: _has_audit_data ? `${label} - ${ev_idx}` : '-',
                                contentType: 'audit_detail',
                                styles: this.factorstables.td
                            });
                            _evaluation_content.push(_row_1st);
                            
                            _factors_v2[_category1_idx].category3[_category3_idex].subs[_sub_index].evaluations.push(..._evaluation_content);
                        }
                    }
                    
                    return _factors_v2;
                }, []);
                
                this.mapSubCodeToScoringPd = mapSubCodeToScoringPd;
                
                for (let _factor_cat1 of _factors_raw) {
                    for (let _factor_cat3 of _factor_cat1.category3) {
                        for (let _factor_subs of _factor_cat3.subs) {
                            const _flatten_sub_evaluations = [..._factor_subs.evaluations].reduce((_flatten, _sub_ev) => {
                                _flatten.push(..._sub_ev);
                                return _flatten;
                            }, []);
                            const {sub_code} = _factor_subs;
                            
                            if(exceptionSubList.includes(_factor_subs.sub_code) ){
                                const [ev1, ev2] = _factor_subs.evaluations;
                                if(ev1[0].status === true && ev2[0].status === false){
                                    _factor_subs.evaluations.splice(1, 1);
                                    if(auditData[sub_code]?.[ev2[0].ev_id]){
                                        delete auditData[sub_code][ev2[0].ev_id];
                                    }
                                }
                                if(ev1[0].status === false && ev2[0].status === true){
                                    _factor_subs.evaluations.splice(0, 1);
                                    if(auditData[sub_code]?.[ev1[0].ev_id]){
                                        delete auditData[sub_code][ev1[0].ev_id];
                                    }
                                }
                            }
                                
                            
                            
                            const _has_importance_evaluation_or_assessment = _flatten_sub_evaluations.some(({contentType}) => ['importance', 'assessment'].includes(contentType));
                            if (_has_importance_evaluation_or_assessment) {
                                for (let _subs_evs of _factor_subs.evaluations) {
                                    let _ev_importance_or_assessment_idx = _subs_evs.findIndex(({contentType}) => contentType && ['importance', 'assessment'].includes(contentType));
                                    if (_ev_importance_or_assessment_idx == -1) {
                                        const _subs_evs_idx = _subs_evs.findIndex(({contentType}) => contentType === 'content');
                                        if (_subs_evs_idx !== -1) {
                                            _subs_evs[_subs_evs_idx].colSpan = 2;
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
                
                this.factors = _factors_raw;
                this.auditData = auditData;
            }
            
        },
        createFactorsPage(doc, position_y) {
            position_y = this.firstElementSpacing;
            
            position_y = this.addTitle(doc, position_y, 1, this.$t('세부 평가결과', this.report_lang));
            
            let last_page_number = doc.internal.getNumberOfPages();
            
            this.factors.forEach((_cat1, _cat1_idx) => {
                if (_cat1_idx > 0 && _cat1_idx < this.factors.length) {
                    position_y += this.jsPDFOpt.spacing.section_sm;
                }
                
                let _is_new_page = this.checkNeedNewPage(doc, position_y, 40); // 35
                if (_is_new_page) {
                    this.addDocPage(doc, true);
                    position_y = this.firstElementSpacing;
                    last_page_number = doc.internal.getNumberOfPages();
                }
                
                position_y = this.addTitle(doc, position_y, 2, `${_cat1_idx + 1}. ${this.$t(_cat1.category1_question_name, this.report_lang)}`, {
                    skip_divider: true,
                    no_padding: true
                });
                
                _cat1.category3.forEach((_cat3) => {
                    // 문항별 Factors 테이블
                    _cat3.subs.forEach((_sub) => {
                        let _is_new_page = this.checkNeedNewPage(doc, position_y, 60); // 80 // 50 // 35
                        if (_is_new_page) {
                            this.addDocPage(doc, true);
                            last_page_number = doc.internal.getNumberOfPages();
                            position_y = this.firstElementSpacing;
                        }
                        
                        position_y = this.addTitle(doc, position_y, 4, `[${_cat3.category3_question_name} - ${_sub.label}]  ${_sub.sub_name}`, {
                            skip_divider: true,
                            no_margin: true,
                            no_padding: true
                        });
                        position_y -= 3;
                        const _flatten_sub_evaluations = [..._sub.evaluations].reduce((_flatten, _sub_ev) => {
                            _flatten.push(..._sub_ev);
                            return _flatten;
                        }, []);
                        
                        const _has_importance_evaluation_or_assessment = !!_flatten_sub_evaluations.find(({contentType}) => ['importance', 'assessment'].includes(contentType));
                        const _columnStyles = {
                            0: {
                                cellWidth: this.report_lang === 'en' ? this.pageContentWidth * 0.12 : this.pageContentWidth * 0.1,
                                halign: 'center',
                            },
                            1: {
                                cellWidth: this.report_lang === 'en' ? this.pageContentWidth * 0.76 : this.pageContentWidth * 0.8,
                            },
                            2: {
                                cellWidth: this.report_lang === 'en' ? this.pageContentWidth * 0.12 : this.pageContentWidth * 0.1,
                                halign: 'center',
                            }
                        };
                        if (_has_importance_evaluation_or_assessment) {
                            _columnStyles[1] = {
                                ...this.tableStyles.td,
                                cellWidth: this.pageContentWidth * 0.12,
                                valign: 'middle',
                                halign: 'left',
                                cellPadding: {top: 5, right: 5, bottom: 5, left: 5}
                            };
                            _columnStyles[2] = {
                                cellWidth: this.report_lang === 'en' ? this.pageContentWidth * 0.64 : this.pageContentWidth * 0.68,
                            };
                            _columnStyles[3] = {
                                cellWidth: this.report_lang === 'en' ? this.pageContentWidth * 0.12 : this.pageContentWidth * 0.1,
                                halign: 'center',
                            };
                        }
                        
                        autoTable(doc, {
                            theme: 'grid',
                            startY: position_y, // - 3,
                            useCss: true,
                            margin: {...this.jsPDFOpt.margin, top: this.firstElementSpacing - 5},
                            body: _sub.evaluations,
                            pageBreak: 'auto',
                            rowPageBreak: 'avoid',
                            head: [[
                                {content: this.$t('충족 여부', this.report_lang), dataKey: 'FactorState'},
                                {
                                    content: this.$t('주요 사항', this.report_lang),
                                    dataKey: 'FactorContent',
                                    colSpan: (_has_importance_evaluation_or_assessment) ? 2 : 1
                                },
                                {
                                    content: this.$t('평가 번호', this.report_lang),
                                    dataKey: 'FactorDetail',
                                }
                            ]],
                            styles: this.factorstables.global,
                            headStyles: this.factorstables.th,
                            bodyStyles: this.factorstables.td,
                            columnStyles: _columnStyles,
                            willDrawCell: (data) => {
                                if (data.section === 'body') {
                                    const {cell} = data;
                                    if (data.column.dataKey === 0) {
                                        cell.text = [];
                                    } else if (data.column.dataKey === 1) {
                                        if (cell.raw.contentType === 'importance' && cell.raw.content) {
                                            cell.text = [];
                                        }
                                    }
                                    if (cell.raw.contentType === 'audit_detail' && cell.raw.content && cell.raw.content !== '-') {
                                        cell.text = [];
                                    }
                                }
                            },
                            didDrawCell: (data) => {
                                if (data.section === 'body') {
                                    const {column, cell, doc} = data;
                                    if (column.dataKey === 0) {
                                        const textColor = '#ffffff';
                                        const _text = cell.raw.content;
                                        const bgColor = cell.raw.status ? this.hyundai_colors.blue : '#dc353c';
                                        if (this.report_lang === 'ca') {
                                            cell.styles.font = this.jsPDFOpt.fontFamily_ca;
                                        } else {
                                            cell.styles.font = this.jsPDFOpt.fontFamily;
                                        }
                                        
                                        // Set text color
                                        doc.setTextColor(textColor);
                                        doc.setFontSize(cell.styles.fontSize - 1.2);
                                        doc.setFillColor(bgColor);
                                        
                                        const [textWidth, textHeight] = [doc.getTextDimensions(_text).w, doc.getTextDimensions(_text).h];
                                        let badgeWidth = this.report_lang === 'en' ? 36 : 24;
                                        let badgeHeight = textHeight + 4.5;
                                        
                                        // if (this.pageContentHeight > cell.y + badgeHeight) {
                                        if (this.pageContentHeight >= cell.y + 1) {
                                            doc.roundedRect(cell.x + ((cell.width - badgeWidth) / 2), cell.y + ((cell.height - badgeHeight) / 2), badgeWidth, badgeHeight, 3, 3, 'F');
                                            
                                            // Draw text in the center of the cell
                                            doc.text(_text, cell.x + cell.width / 2, cell.y + (cell.height / 2) + 0.15, {
                                                baseline: 'middle',
                                                align: 'center'
                                            });
                                        }
                                    } else if (column.dataKey === 1) {
                                        if (cell.raw.contentType === 'importance' && cell.raw.content) {
                                            // Set text color
                                            doc.setTextColor('#ffffff');
                                            doc.setFontSize(this.report_lang === 'ko' ? cell.styles.fontSize - 1.5 : cell.styles.fontSize - 2);
                                            doc.setFillColor('#ff5722');
                                            
                                            const _text = cell.raw.content;
                                            const [textWidth, textHeight] = [doc.getTextDimensions(_text).w, doc.getTextDimensions(_text).h];
                                            let [badgeWidth, badgeHeight] = [textWidth + 10, textHeight + 4.5];
                                            if (badgeWidth >= cell.width) {
                                                badgeWidth += textWidth + 3;
                                            }
                                            
                                            doc.roundedRect(cell.x + ((cell.width - badgeWidth) / 2), cell.y + ((cell.height - badgeHeight) / 2), badgeWidth, badgeHeight, 3, 3, 'F');
                                            
                                            // Draw text in the center of the cell
                                            doc.text(_text, cell.x + cell.width / 2, cell.y + (cell.height / 2) + 0.15, {
                                                baseline: 'middle',
                                                align: 'center'
                                            });
                                            
                                        } else if (cell.raw.contentType === 'content' && cell.raw.content) {
                                            if (this.report_lang === 'ca') {
                                                cell.styles.font = this.jsPDFOpt.fontFamily_ca;
                                            } else {
                                                cell.styles.font = this.jsPDFOpt.fontFamily;
                                            }
                                        }
                                    } else if (column.dataKey === 2 && cell?.raw?.contentType === 'content') {
                                        if (this.report_lang === 'ca') {
                                            cell.styles.font = this.jsPDFOpt.fontFamily_ca;
                                        } else {
                                            cell.styles.font = this.jsPDFOpt.fontFamily;
                                        }
                                    } else if (column.dataKey === 3 && cell?.raw?.contentType === 'content') {
                                        
                                        if (this.report_lang === 'ca') {
                                            cell.styles.font = this.jsPDFOpt.fontFamily_ca;
                                        } else {
                                            cell.styles.font = this.jsPDFOpt.fontFamily;
                                        }
                                    }
                                    if (cell.raw.contentType === 'audit_detail' && cell.raw.content && cell.raw.content !== '-') {
                                        this.linkDestinationInfo.toAudit[cell?.raw?.content] = {
                                            linkSource: {
                                                pageNumber: doc.internal.getCurrentPageInfo().pageNumber,
                                                x: cell.x,
                                                y: cell.y,
                                                width: cell.width,
                                                height: cell.height
                                            },
                                            linkDestination: {}
                                        };
                                        this.linkDestinationInfo.toFactor[cell?.raw?.content] = {
                                            linkSource: {},
                                            linkDestination: {
                                                pageNumber: doc.internal.getCurrentPageInfo().pageNumber,
                                                y: cell.y
                                            }
                                        };
                                        
                                        
                                        const _text = cell.raw.content;
                                        const [textWidth, textHeight] = [doc.getTextDimensions(_text).w, doc.getTextDimensions(_text).h];
                                        
                                        const position_x = cell.x + (cell.width / 2);
                                        const position_y = cell.y + (cell.height / 2);
                                        
                                        doc.text(cell.raw.content, position_x, position_y + 0.15, {
                                            baseline: 'middle',
                                            align: 'center'
                                        });
                                        doc.setDrawColor('#292828');
                                        doc.line(position_x - (textWidth / 2), position_y + (textHeight / 2), position_x + (textWidth / 2), position_y + (textHeight / 2)); // (x1, y1, x2, y2)
                                    }
                                }
                            },
                            willDrawPage: (HookData) => {
                                if(last_page_number !== HookData.doc.internal.getNumberOfPages()){
                                    this.addDocPage(HookData.doc, true);
                                    last_page_number = HookData.doc.internal.getNumberOfPages();
                                }
                            },
                            didDrawPage: (HookData) => {
                                this.addHeader(HookData.doc);
                            }
                        });
                        
                        this.setFont(doc, null);
                        position_y = doc.lastAutoTable.finalY;
                        
                        position_y += this.jsPDFOpt.spacing.section_xs;
                    });
                });
            });
            
            return position_y;
        },
        createAuditPage(doc, position_y) {
            this.addDocPage(doc);
            this.addHeader(doc);
            
            let last_page_number = doc.internal.getNumberOfPages();
            
            
            position_y = this.firstElementSpacing;
            position_y = this.addTitle(doc, position_y, 1, this.$t('별지: 평가 세부 내용', this.report_lang));
            
            Object.values(this.auditData).forEach((sub_items) => {
                const sub_items_list = Object.values(sub_items);
                
                if (sub_items_list?.length < 1) {
                    
                    return;
                }
                
                let _is_new_page = this.checkNeedNewPage(doc, position_y, 30); // 80 // 50 // 35
                if (_is_new_page) {
                    this.addDocPage(doc, true);
                    last_page_number = doc.internal.getNumberOfPages();
                    position_y = this.firstElementSpacing;
                }
                
                sub_items_list.sort((a, b) => a.ev_idx - b.ev_idx);
                
                const table_rows = [];
                const table_head = [[
                    {content: this.$t('평가 번호', this.report_lang)},
                    {content: this.$t('평가 카테고리', this.report_lang)},
                    {content: this.$t('평가 내용', this.report_lang)}
                ]];
                
                const _columnStyles = {
                    0: {
                        cellWidth: this.report_lang === 'en' ? this.pageContentWidth * 0.16 : this.pageContentWidth * 0.1,
                        halign: 'center',
                    },
                    1: {
                        cellWidth: this.pageContentWidth * 0.25,
                        halign: 'center',
                    },
                    2: {
                        cellWidth: this.report_lang === 'en' ? this.pageContentWidth * 0.59 : this.pageContentWidth * 0.65,
                    }
                };
                
                sub_items_list.forEach(item => {
                    const {
                        label,
                        ev_idx,
                        audit_category_code,
                        audit_comment,
                        audit_comment_ea,
                        audit_comment_ca
                    } = item;
                    const target_audit_category = this.audit_category.find(cat => cat.code === audit_category_code);
                    const category_text = this.report_lang === 'ko' ? target_audit_category?.category : this.report_lang === 'en' ? target_audit_category?.category_ea : target_audit_category?.category_ca;
                    const audit_comment_text = this.report_lang === 'ko' ? audit_comment : this.report_lang === 'en' ? audit_comment_ea : audit_comment_ca;
                    
                    table_rows.push([
                        {content: `${label} - ${ev_idx}`, styles: this.factorstables.td, contentType: 'audit_detail'},
                        {content: category_text, styles: this.factorstables.td},
                        {content: audit_comment_text || '', styles: this.factorstables.td},
                    ]);
                });
                
                
                
                const {
                    category3_question_name,
                    label,
                    sub_name,
                    review_comment,
                    review_comment_ca,
                    review_comment_ea,
                    reviewer_name
                } = sub_items_list?.[0];
                
                position_y = this.addTitle(doc, position_y, 4, `[${category3_question_name} - ${label}]  ${sub_name}`, {
                    skip_divider: true,
                    no_margin: true,
                    no_padding: true
                });
                position_y -= 3;
                
                autoTable(doc, {
                    theme: 'grid',
                    startY: position_y, // - 3,
                    useCss: true,
                    margin: {...this.jsPDFOpt.margin, top: this.firstElementSpacing - 5},
                    body: table_rows,
                    rowPageBreak: 'auto',
                    head: table_head,
                    styles: this.factorstables.global,
                    headStyles: this.factorstables.th,
                    bodyStyles: this.factorstables.td,
                    columnStyles: _columnStyles,
                    willDrawCell: (data) => {
                        if (data.section === 'body') {
                            const {cell} = data;
                            if (cell.raw.contentType === 'audit_detail') {
                                cell.text = [];
                            }
                        }
                    },
                    didDrawCell: (data) => {
                        if (data.section === 'body') {
                            const {cell} = data;
                            if (this.report_lang === 'ca') {
                                cell.styles.font = this.jsPDFOpt.fontFamily_ca;
                            } else {
                                cell.styles.font = this.jsPDFOpt.fontFamily;
                            }
                            
                            if (cell.raw.contentType === 'audit_detail') {
                                this.linkDestinationInfo.toAudit[cell?.raw?.content].linkDestination = {
                                    pageNumber: doc.internal.getCurrentPageInfo().pageNumber,
                                    y: cell.y
                                };
                                this.linkDestinationInfo.toFactor[cell?.raw?.content].linkSource = {
                                    pageNumber: doc.internal.getCurrentPageInfo().pageNumber,
                                    x: cell.x,
                                    y: cell.y,
                                    width: cell.width,
                                    height: cell.height
                                };
                                
                                const _text = cell.raw.content;
                                const [textWidth, textHeight] = [doc.getTextDimensions(_text).w, doc.getTextDimensions(_text).h];
                                
                                const position_x = cell.x + (cell.width / 2);
                                const position_y = cell.y + (cell.height / 2);
                                
                                doc.text(cell.raw.content, position_x, position_y + 0.15, {
                                    baseline: 'middle',
                                    align: 'center'
                                });
                                doc.setDrawColor('#292828');
                                doc.line(position_x - (textWidth / 2), position_y + (textHeight / 2), position_x + (textWidth / 2), position_y + (textHeight / 2)); // (x1, y1, x2, y2)
                            }
                        }
                    },
                    willDrawPage: (HookData) => {
                        if(last_page_number !== HookData.doc.internal.getNumberOfPages()){
                            this.addDocPage(HookData.doc, true);
                            last_page_number = HookData.doc.internal.getNumberOfPages();
                        }
                    },
                    didDrawPage: (HookData) => {
                        this.addHeader(HookData.doc);
                    }
                });
                
                this.setFont(doc, null);
                position_y = doc.lastAutoTable.finalY;
                
                if (review_comment != null) {
                    position_y += 6;
                    let _is_new_page = this.checkNeedNewPage(doc, position_y, 80);
                    if (_is_new_page) {
                        this.addDocPage(doc, true);
                        position_y = this.firstElementSpacing;
                    }
                    
                    position_y = this.addTitle(doc, position_y, 4, this.$t('종합 의견', this.report_lang), {
                        skip_divider: true,
                        no_margin: true,
                        no_padding: true
                    });
                    position_y -= 3;
                    const review_comment_text = this.report_lang === 'ko' ? review_comment : this.report_lang === 'en' ? review_comment_ea : review_comment_ca;
                    
                    
                    const review_padding = 5;
                    const review_width = this.pageContentWidth;
                    const review_text = doc.splitTextToSize(review_comment_text, review_width - (review_padding * 2));
                    
                    const review_text_dim = doc.getTextDimensions(review_text);
                    
                    const review_box_height = review_text_dim.h + (review_padding * 2);
                    
                    
                    doc.setFillColor(...this.jsPDFOpt.card.fillColor);
                    doc.setLineWidth(this.jsPDFOpt.card.lineWidth * 2);
                    doc.setDrawColor(this.jsPDFOpt.card.borderColor);
                    doc.rect(this.jsPDFOpt.margin.left, position_y, review_width, review_box_height, 'FD');
                    
                    this.setFont(doc, null, null, 'normal');
                    doc.setFontSize(9);
                    doc.text(review_text || '', this.jsPDFOpt.margin.left + review_padding, position_y + 12);
                    
                    position_y += review_box_height + 10;
                    
                }
                
                position_y += 10;
                
            });
            const pageHeight = doc.internal.pageSize.getHeight();
            ['toAudit', 'toFactor'].forEach((key) => {
                Object.values(this.linkDestinationInfo[key]).forEach(({linkSource, linkDestination}) => {
                    if (linkSource && linkDestination) {
                        // 링크를 그려야 할 출발지 페이지로 다시 이동
                        doc.setPage(linkSource.pageNumber);
                        
                        // 저장해둔 정보를 바탕으로 링크 생성
                        doc.link(
                            linkSource.x,
                            linkSource.y,
                            linkSource.width,
                            linkSource.height,
                            {
                                pageNumber: linkDestination.pageNumber,
                                top: linkDestination.y
                            }
                        );
                    }
                });
            });
        },
        addDocPage(doc, alreadyAdded = false){
            if(!alreadyAdded)
                doc.addPage();
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            doc.addImage(background_texture_img, 'PNG', 0, 0, pageWidth, pageHeight, undefined, 'FAST');
        },
        generateCover_HMC(doc) {
            try {
                // 페이지 너비,높이
                const pageWidth = doc.internal.pageSize.getWidth();
                const pageHeight = doc.internal.pageSize.getHeight();
                
                const maxWidth = pageWidth - 20; // 페이지의 여백을 고려한 너비
                const maxHeight = pageHeight - 20; // 페이지의 여백을 고려한 높이
                
                // 표지 색상 설정
                doc.setFillColor(this.hyundai_colors.blue); // RGB 값
                doc.rect(0, 0, pageWidth, pageHeight, 'F'); // 'F'는 fill
                
                const cover_hyundai_logo_position_x = 30;
                const cover_hyundai_logo_position_y = 40;
                
                const hyundaiLogoSize = {
                    width: 235 / 4, height: 30 / 4
                };
                const kiaLogoSize = {
                    width: 1060 / 16, height: 277 / 16
                };
                const qesgLogoSize = {
                    width : 3747 / 45, height: 942 / 45
                };
                
                // 현대&기아 로고 추가
                doc.addImage(HYUNDAILogoPNG, 'png', cover_hyundai_logo_position_x, cover_hyundai_logo_position_y, hyundaiLogoSize.width * 1.4, hyundaiLogoSize.height * 1.4);
                doc.setTextColor('#FFFFFF');
                doc.setFontSize(20);
                doc.text('•',cover_hyundai_logo_position_x + 92, cover_hyundai_logo_position_y+ 11);
                doc.addImage(KIALogoPNG, 'png', cover_hyundai_logo_position_x + 100, cover_hyundai_logo_position_y - 4, kiaLogoSize.width, kiaLogoSize.height);

                // QESG 로고 추가
                doc.addImage(QESGLogoWhite, 'png', 333, cover_hyundai_logo_position_y - 5, qesgLogoSize.width, qesgLogoSize.height);
                
                
                let _text_position_y = (maxHeight / 3) + (this.jsPDFOpt.margin.top * 1.25);
                let _text_position_x = this.jsPDFOpt.margin.left;
                
                const additional_info_position_y = _text_position_y - 40;
                // "재평가 결과보고서" 문구 추가
                if (this.is_reassessment) {
                    this.setFont(doc, 'title');
                    doc.setTextColor('#FFFFFF');
                    const _reassessment_report_title = doc.splitTextToSize(`[${this.$t('재평가 결과보고서', this.report_lang)}]`, this.pageContentWidth);
                    doc.text(_reassessment_report_title, _text_position_x, additional_info_position_y, { align: 'left' });
                }
                
                // 심화평가 문구 추가 ("재평가 결과보고서" 문구가 추가되면 심화평가 문구는 추가 안됨)
                if (!this.is_reassessment && ((this.assessmentType && this.assessmentType === 'OSIS') || (!this.assessmentType && this.reportData.info.service_type === '심화'))) {
                    this.setFont(doc, 'title');
                    doc.setTextColor('#FFFFFF');
                    const _osis_report_title = doc.splitTextToSize(`[${this.$t('실사평가 결과보고서', this.report_lang)}]`, this.pageContentWidth);
                    doc.text(_osis_report_title, _text_position_x, additional_info_position_y, { align: 'left' });
                }
                
                // 보고서 이름 추가
                this.setFont(doc, 'cover_name_lg');
                doc.setTextColor('#FFFFFF');
                const [_report_title_width, _report_title_height] = [doc.getTextDimensions(this.cover_title).w, doc.getTextDimensions(this.cover_title).h];
                
                
                doc.text(this.cover_title, _text_position_x, _text_position_y, {});
                
                // 보고서 기업명 추가
                if (this.report_lang === 'ca' && this.isChineseOrHanzi(this.companyInfo['회사명'])) {
                    this.setFont(doc, 'cover_company_name', this.jsPDFOpt.fontFamily_ca);
                } else {
                    doc.setFont();
                    this.setFont(doc, 'cover_company_name', this.jsPDFOpt.fontFamily);
                }
                
                const _company_name = doc.splitTextToSize(this.companyInfo['회사명'], this.pageContentWidth);
                _text_position_y += _report_title_height + (this.jsPDFOpt.margin.top * 0.75);
                doc.setTextColor('#FFFFFF');
                doc.text(_company_name, _text_position_x, _text_position_y);
                
                // disclaimer 추가
                /**
                 * 969696
                 */
                this.setFont(doc, 'title_sm', this.jsPDFOpt.fontFamily);
                doc.setTextColor('#FFFFFF');
                doc.text('Disclaimer', 30, maxHeight - 65, {});
                
                doc.setDrawColor('#FFFFFF'); // RGB 값
                doc.setLineWidth(0.5);                // 굵은 선
                doc.line(28, maxHeight - 60, this.report_lang === 'en' ? 300 : 280, maxHeight - 60); // 좌측 20~우측 190, y=40 위치에 가로선
                
                const texts = [
                    `- ${this.$t('result_report_disclaimer.text1', this.report_lang)}`,
                    `   ${this.$t('result_report_disclaimer.text2', this.report_lang)}`,
                    `- ${this.$t('result_report_disclaimer.text3', this.report_lang)}`,
                    `   ${this.$t('result_report_disclaimer.text4', this.report_lang)}`,
                    `- ${this.$t('result_report_disclaimer.text5', this.report_lang)}`,
                ];
                
                // 각 줄별로 출력 (들여쓰기 반영)
                const lineHeight = 9;
                const leftMargin = 30;
                let y = maxHeight - 50; // 시작 위치
                texts.forEach((line, idx) => {
                    
                    this.setFont(doc, 'footnote', this.jsPDFOpt.fontFamily);
                    doc.setTextColor('#FFFFFF');
                    doc.text(line, leftMargin, y);
                    y += lineHeight;
                });
            } catch (error) {
                console.log(error);
            }
        },
        async generatePDF_hmc_2025(doc, position_top) {
            // Factors 페이지 데이터 생성
            this.generateFactorsAndAuditData();
            
            this.pageContentWidth = doc.internal.pageSize.width - (this.jsPDFOpt.margin.left + this.jsPDFOpt.margin.right);
            this.pageContentHeight = doc.internal.pageSize.height - (this.jsPDFOpt.margin.top + this.jsPDFOpt.margin.bottom);
            
            this.generateCover_HMC(doc);
            
            this.addDocPage(doc);
            this.addHeader(doc, 2);
            
            let position_y = await this.createSummaryPage_v4(doc, position_top);
            position_y = this.generateKeyFigurePage(doc, position_y);
            // position_y = await this.generatePACFPFAnalticsPage(doc, position_y);
            position_y = await this.generateScoreCardViewPage(doc, position_y);
            position_y = await this.generatePartialSummaryPage1(doc, position_y);
            position_y = await this.generatePartialSummaryPage2(doc, position_y);
            position_y = await this.generateCategory3AnalysisPage(doc, position_y);
            
            if (this.reportMode === 'full') {
                this.addDocPage(doc);
                this.addHeader(doc);
                this.createFactorsPage(doc, position_y);
                this.createAuditPage(doc, position_y);
            }
            
            const totalPages = doc.getNumberOfPages();
            for (let i = 2; i <= totalPages; i++) {
                doc.setPage(i);  // 현재 페이지로 이동
                this.addFooter(doc);
                
                
            }
            
            doc.setDocumentProperties({
                title: this.pdf_filename,
                subject: this.pdf_filename,
                filename: `${this.pdf_filename}.pdf`,
                author: 'QuantifiedESG Inc.',
                creator: 'QESG'
            });
            
            return doc;
        }
    }
};
